import {
  analyzeLap,
  analyzeSession,
  buildMapPointsForGpsWindow,
  downsampleTrackPoints,
  gpsPointsInWindow,
  isMyChronSessionExport,
  lapWindow,
  racingLapsForAnalysis,
  sessionGpsCenter,
  type MyChronGpsPoint,
  type MyChronSessionExport,
  type ReplayLapWindow,
  type ReplayTimeline,
  type TrackMapPoint,
} from './telemetrySession';
import type { SavedTrack, TrackContribution as SavedTrackContribution } from '@kangaroo/shared';
import { findNearestTrack, trackDistanceKm } from './trackGeo';
import { selectPrimaryGoProGpsSegment } from './goproTelemetry';

// ── Data model ──────────────────────────────────────────────────────────────

export type TrackDataLayer = 'draft' | 'global';

export type GpsCoord = { lat: number; lon: number };

export type StartLineSuggestion = {
  pointA: GpsCoord;
  pointB: GpsCoord;
  confidence: number;
};

export type SegmentSuggestion = {
  id: string;
  label: string;
  startFraction: number;
  endFraction: number;
  confidence: number;
};

export type CurveType =
  | 'hairpin'
  | 'sharp'
  | 'sweeper'
  | 'chicane'
  | 'kink'
  | 'complex'
  | 'unknown';

export const CURVE_TYPE_LABELS: Record<CurveType, string> = {
  hairpin: 'Hairpin',
  sharp: 'Sharp',
  sweeper: 'Sweeper',
  chicane: 'Chicane',
  kink: 'Kink',
  complex: 'Complex',
  unknown: 'Unknown',
};

/** Smoothing + threshold bundle used when detecting a specific bend shape. */
export type CurveDetectionProfileConfig = {
  label: string;
  apexSmoothWindow: number;
  deflectionSmoothWindow: number;
  deflectionRadius: number;
  arcTurnRadius: number;
  minLocalCurvatureDeg: number;
  minArcTurnDeg: number;
  minDeflectionScore: number;
  minRawCurvatureDeg: number;
  minSeparationM: number;
  minChicaneKeepSeparationM: number;
  useSignedPeaks: boolean;
  candidateDedupDistanceM: number;
};

export type DetectableCurveType = Exclude<CurveType, 'complex' | 'unknown'>;

export const CURVE_DETECTION_PROFILES: Record<DetectableCurveType, CurveDetectionProfileConfig> = {
  hairpin: {
    label: 'Hairpin',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 3,
    deflectionRadius: 3,
    arcTurnRadius: 3,
    minLocalCurvatureDeg: 10,
    minArcTurnDeg: 28,
    minDeflectionScore: 24,
    minRawCurvatureDeg: 7,
    minSeparationM: 18,
    minChicaneKeepSeparationM: 8,
    useSignedPeaks: false,
    candidateDedupDistanceM: 5,
  },
  sharp: {
    label: 'Sharp',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 5,
    deflectionRadius: 4,
    arcTurnRadius: 4,
    minLocalCurvatureDeg: 8,
    minArcTurnDeg: 16,
    minDeflectionScore: 24,
    minRawCurvatureDeg: 6,
    minSeparationM: 26,
    minChicaneKeepSeparationM: 9,
    useSignedPeaks: false,
    candidateDedupDistanceM: 8,
  },
  sweeper: {
    label: 'Sweeper',
    apexSmoothWindow: 9,
    deflectionSmoothWindow: 11,
    deflectionRadius: 8,
    arcTurnRadius: 6,
    minLocalCurvatureDeg: 6,
    minArcTurnDeg: 14,
    minDeflectionScore: 20,
    minRawCurvatureDeg: 4,
    minSeparationM: 38,
    minChicaneKeepSeparationM: 12,
    useSignedPeaks: false,
    candidateDedupDistanceM: 14,
  },
  chicane: {
    label: 'Chicane',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 3,
    deflectionRadius: 2,
    arcTurnRadius: 2,
    minLocalCurvatureDeg: 7,
    minArcTurnDeg: 10,
    minDeflectionScore: 16,
    minRawCurvatureDeg: 5,
    minSeparationM: 18,
    minChicaneKeepSeparationM: 8,
    useSignedPeaks: true,
    candidateDedupDistanceM: 6,
  },
  kink: {
    label: 'Kink',
    apexSmoothWindow: 3,
    deflectionSmoothWindow: 3,
    deflectionRadius: 2,
    arcTurnRadius: 2,
    minLocalCurvatureDeg: 5,
    minArcTurnDeg: 8,
    minDeflectionScore: 14,
    minRawCurvatureDeg: 4,
    minSeparationM: 22,
    minChicaneKeepSeparationM: 8,
    useSignedPeaks: false,
    candidateDedupDistanceM: 6,
  },
};

const DETECTION_PROFILE_ORDER: DetectableCurveType[] = ['hairpin', 'sharp', 'chicane'];

const PROFILE_PRIORITY: Record<DetectableCurveType, number> = {
  hairpin: 5,
  sharp: 4,
  chicane: 3,
  sweeper: 2,
  kink: 1,
};

/** Minimum spacing between corners unless they form a confirmed chicane pair. */
const GLOBAL_MIN_CORNER_SEPARATION_M = 24;
const CHICANE_PAIR_MIN_SEPARATION_M = 8;
const CHICANE_PAIR_MAX_SEPARATION_M = 22;

export type CornerApexSuggestion = {
  id: string;
  label: string;
  lat: number;
  lon: number;
  turnNumber: number;
  confidence: number;
  /** Classified bend geometry after detection. */
  curveType?: CurveType;
  /** Which detection profile first surfaced this apex. */
  detectionProfile?: DetectableCurveType;
};

export type CornerDetectionOptions = {
  kerbLines?: GpsCoord[][];
};

export function cornerKerbEvidenceScore(distanceM: number) {
  return 18 * Math.exp(-(distanceM ** 2) / (2 * 14 ** 2));
}

export type TrackContributionPayload = {
  trackId: string;
  gpsPolyline: GpsCoord[];
  startLineSuggestion: StartLineSuggestion;
  segmentSuggestions: SegmentSuggestion[];
  cornerApexSuggestions: CornerApexSuggestion[];
  direction: 'clockwise' | 'counterclockwise';
  lapLengthEstimateM: number;
  confidenceScore: number;
  startFraction?: number;
};

export type ContributionStatus = 'draft' | 'pending' | 'reviewed' | 'accepted' | 'rejected' | 'merged';

export type TrackContribution = {
  id: string;
  sessionKey: string;
  venue: string;
  status: ContributionStatus;
  payload: TrackContributionPayload;
  qualityScore: number;
  matchedTrackName: string;
  matchConfidence: number;
  findings: EnrichmentFinding[];
  createdAt: string;
};

export type EnrichmentFinding = {
  id: string;
  kind: 'start_line' | 'outline' | 'corner' | 'segment';
  title: string;
  detail: string;
  confidence: number;
  improvementScore: number;
};

export type TrackProfile = {
  id: string;
  name: string;
  aliases: string[];
  center: GpsCoord;
  officialRevision: TrackContributionPayload;
  communitySuggestions: TrackContribution[];
  teamOverrides: TrackContributionPayload[];
};

export type LapQualityBreakdown = {
  lapNumber: number;
  qualityScore: number;
  gpsQuality: number;
  lapCompleteness: number;
  lineConsistency: number;
  speedConsistency: number;
  sampleDensity: number;
  accepted: boolean;
  rejectReason?: string;
};

export type EnrichmentPipelineStep = {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'skipped' | 'failed';
  detail?: string;
};

export type TrackMatchMethod =
  | 'session-context'
  | 'circuit-draft'
  | 'name'
  | 'gps'
  | 'catalog'
  | 'manual'
  | 'unknown';

export type ResolvedTrackMatch = {
  id: string;
  name: string;
  confidence: number;
  method: TrackMatchMethod;
};

export type CircuitDraftMatch = {
  draftId: string;
  trackId: string;
  circuitName: string;
  direction: 'clockwise' | 'counterclockwise';
  confidence: number;
  meanDistanceM: number;
  directionAligned: boolean;
  payload: SavedTrackContribution['payload'];
};

export type TrackMatchOptions = {
  savedTracks?: SavedTrack[];
  preferredTrackId?: string | null;
  /** User-selected track override from the enrichment UI. */
  manualTrackId?: string | null;
  /** Best geometry match from saved circuit drafts (shape + direction). */
  circuitDraftMatch?: CircuitDraftMatch | null;
};

export type EnrichmentPipelineResult = {
  sessionKey: string;
  venue: string;
  location: GpsCoord | null;
  matchedTrack: ResolvedTrackMatch;
  lapQualities: LapQualityBreakdown[];
  acceptedLapNumbers: number[];
  contribution: TrackContribution;
  globalProfile: TrackProfile;
  steps: EnrichmentPipelineStep[];
};

// ── Track catalog (PoC seed) ───────────────────────────────────────────────

const TRACK_CATALOG: Array<{
  id: string;
  name: string;
  aliases: string[];
  center: GpsCoord;
}> = [
  {
    id: 'cwn-full',
    name: 'CWFULL',
    aliases: ['CW FULL', 'Cal Club Full'],
    center: { lat: 36.16451, lon: -115.8969 },
  },
];

export function listCatalogTrackOptions() {
  return TRACK_CATALOG.map((track) => ({ id: track.id, name: track.name }));
}

const EARTH_RADIUS_M = 6_371_000;

function haversineM(a: GpsCoord, b: GpsCoord) {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

function bearingDeg(from: GpsCoord, to: GpsCoord) {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLon = ((to.lon - from.lon) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function normalizeAngleDelta(deg: number) {
  const wrapped = ((deg + 180) % 360 + 360) % 360 - 180;
  return Math.abs(wrapped);
}

function resamplePolylineByDistance(points: GpsCoord[], spacingM: number): GpsCoord[] {
  if (points.length < 2) {
    return points;
  }

  const cumulative = [0];
  for (let i = 1; i < points.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + haversineM(points[i - 1]!, points[i]!));
  }

  const total = cumulative.at(-1) ?? 0;
  if (total <= spacingM) {
    return points;
  }

  const result: GpsCoord[] = [points[0]!];
  let target = spacingM;
  let cursor = 0;

  while (target < total) {
    while (cursor < cumulative.length - 2 && cumulative[cursor + 1]! < target) {
      cursor += 1;
    }
    const start = cumulative[cursor]!;
    const end = cumulative[cursor + 1]!;
    const t = end > start ? (target - start) / (end - start) : 0;
    const a = points[cursor]!;
    const b = points[cursor + 1]!;
    result.push({
      lat: a.lat + (b.lat - a.lat) * t,
      lon: a.lon + (b.lon - a.lon) * t,
    });
    target += spacingM;
  }

  const last = points.at(-1)!;
  if (haversineM(result.at(-1)!, last) > 1) {
    result.push(last);
  }

  return result;
}

/** Evenly space polyline vertices for smooth map rendering and storage. */
export function densifyPolylineForDisplay(
  points: GpsCoord[],
  spacingM = 2.5,
  maxPoints = 800,
): GpsCoord[] {
  if (points.length < 2) {
    return points;
  }

  const densified = resamplePolylineByDistance(points, spacingM);
  if (densified.length <= maxPoints) {
    return densified;
  }

  return resamplePolylineByFraction(densified, maxPoints);
}

function closeCircuitPolyline(points: GpsCoord[]): GpsCoord[] {
  if (points.length < 2) {
    return points;
  }

  const first = points[0]!;
  const last = points.at(-1)!;
  if (haversineM(first, last) <= 3) {
    return points;
  }

  return [...points, first];
}

/** Densify and close the circuit loop for map display. */
export function healCircuitPolylineForDisplay(
  points: GpsCoord[],
  spacingM = 2.5,
  maxPoints = 800,
): GpsCoord[] {
  if (points.length < 2) {
    return points;
  }

  const densified = densifyPolylineForDisplay(points, spacingM, maxPoints);
  return closeCircuitPolyline(densified);
}

/** Best flying lap GPS — smooth circuit reference for map overlay. */
export function buildCircuitBestLineFromFlyingLaps(
  session: MyChronSessionExport,
  flyingLaps: ReslicedLapWindow[],
): GpsCoord[] {
  if (flyingLaps.length === 0) {
    return [];
  }

  const bestLap = flyingLaps.reduce((best, lap) => (lap.durationSec < best.durationSec ? lap : best));
  const coords = gpsToCoords(gpsPointsInWindow(session.gps, bestLap.startMs, bestLap.endMs));
  if (coords.length < 2) {
    return [];
  }

  return closeCircuitPolyline(densifyPolylineForDisplay(coords, 2, 900));
}

function smoothValues(values: number[], windowSize: number) {
  if (values.length === 0) {
    return values;
  }

  const half = Math.floor(windowSize / 2);
  return values.map((_, index) => {
    let sum = 0;
    let count = 0;
    for (let offset = -half; offset <= half; offset += 1) {
      const sample = values[index + offset];
      if (sample === undefined) {
        continue;
      }
      sum += sample;
      count += 1;
    }
    return count > 0 ? sum / count : 0;
  });
}

function curvatureDeg(a: GpsCoord, b: GpsCoord, c: GpsCoord) {
  const incoming = bearingDeg(a, b);
  const outgoing = bearingDeg(b, c);
  return normalizeAngleDelta(outgoing - incoming);
}

function signedCurvatureDeg(a: GpsCoord, b: GpsCoord, c: GpsCoord) {
  const incoming = bearingDeg(a, b);
  const outgoing = bearingDeg(b, c);
  return ((outgoing - incoming + 180) % 360 + 360) % 360 - 180;
}

export function findNearestPolylineFraction(polyline: GpsCoord[], point: GpsCoord) {
  if (polyline.length === 0) {
    return { fraction: 0, index: 0, coord: point, distanceM: Number.POSITIVE_INFINITY };
  }

  let bestIndex = 0;
  let bestCoord = polyline[0]!;
  let bestDistance = haversineM(point, polyline[0]!);

  for (let index = 0; index < polyline.length; index += 1) {
    const coord = polyline[index]!;
    const distance = haversineM(point, coord);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
      bestCoord = coord;
    }
  }

  const cumulative = [0];
  for (let i = 1; i < polyline.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + haversineM(polyline[i - 1]!, polyline[i]!));
  }
  const total = cumulative.at(-1) ?? 1;

  return {
    fraction: total > 0 ? cumulative[bestIndex]! / total : 0,
    index: bestIndex,
    coord: bestCoord,
    distanceM: bestDistance,
  };
}

const CIRCUIT_CORRIDOR_MAX_DEVIATION_M = 32;
const CIRCUIT_CORRIDOR_MIN_OFF_RUN = 4;
const CIRCUIT_CORRIDOR_BRIDGE_GAP = 2;

function distancePointToSegmentM(point: GpsCoord, start: GpsCoord, end: GpsCoord) {
  const origin = {
    lat: (start.lat + end.lat) / 2,
    lon: (start.lon + end.lon) / 2,
  };
  const a = toLocalMeters(origin, start);
  const b = toLocalMeters(origin, end);
  const p = toLocalMeters(origin, point);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq <= 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(p.x - projX, p.y - projY);
}

export function distanceToPolylineM(point: GpsCoord, polyline: GpsCoord[]) {
  if (polyline.length === 0) {
    return Number.POSITIVE_INFINITY;
  }

  if (polyline.length === 1) {
    return haversineM(point, polyline[0]!);
  }

  let minDistance = Number.POSITIVE_INFINITY;
  for (let index = 1; index < polyline.length; index += 1) {
    minDistance = Math.min(minDistance, distancePointToSegmentM(point, polyline[index - 1]!, polyline[index]!));
  }

  return minDistance;
}

export function isTrackPointOnCircuitCorridor(
  point: { lat: number; lon: number },
  referencePolyline: GpsCoord[],
  maxDeviationM = CIRCUIT_CORRIDOR_MAX_DEVIATION_M,
) {
  return distanceToPolylineM({ lat: point.lat, lon: point.lon }, referencePolyline) <= maxDeviationM;
}

export function splitTrackMapPointsOffCircuit(
  points: TrackMapPoint[],
  referencePolyline: GpsCoord[],
  options?: {
    maxDeviationM?: number;
    minOffRunPoints?: number;
    bridgeGapPoints?: number;
  },
): TrackMapPoint[][] {
  const maxDeviationM = options?.maxDeviationM ?? CIRCUIT_CORRIDOR_MAX_DEVIATION_M;
  const minOffRunPoints = options?.minOffRunPoints ?? CIRCUIT_CORRIDOR_MIN_OFF_RUN;
  const bridgeGapPoints = options?.bridgeGapPoints ?? CIRCUIT_CORRIDOR_BRIDGE_GAP;

  if (points.length < 2 || referencePolyline.length < 2) {
    return points.length >= 2 ? [points] : [];
  }

  const onCircuit = points.map((point) => isTrackPointOnCircuitCorridor(point, referencePolyline, maxDeviationM));
  const groups: TrackMapPoint[][] = [];
  let current: TrackMapPoint[] = [];
  let index = 0;

  while (index < points.length) {
    if (onCircuit[index]) {
      current.push(points[index]!);
      index += 1;
      continue;
    }

    let end = index;
    while (end < points.length && !onCircuit[end]) {
      end += 1;
    }

    const offRunLength = end - index;
    if (offRunLength <= bridgeGapPoints) {
      for (let bridgeIndex = index; bridgeIndex < end; bridgeIndex += 1) {
        current.push(points[bridgeIndex]!);
      }
    } else if (offRunLength < minOffRunPoints) {
      for (let bridgeIndex = index; bridgeIndex < end; bridgeIndex += 1) {
        current.push(points[bridgeIndex]!);
      }
    } else if (current.length >= 2) {
      groups.push(current);
      current = [];
    } else {
      current = [];
    }

    index = end;
  }

  if (current.length >= 2) {
    groups.push(current);
  }

  return groups.length > 0 ? groups : points.length >= 2 ? [points] : [];
}

export function buildFlyingLapReferencePolyline(
  session: MyChronSessionExport,
  flyingLaps: ReslicedLapWindow[],
  maxPoints = 220,
): GpsCoord[] {
  const coords: GpsCoord[] = [];

  for (const lap of flyingLaps) {
    coords.push(...gpsToCoords(gpsPointsInWindow(session.gps, lap.startMs, lap.endMs)));
  }

  if (coords.length < 2) {
    return coords;
  }

  return downsampleTrackPoints(coords, maxPoints) as GpsCoord[];
}

export function rotatePolylineToStart(polyline: GpsCoord[], startFraction: number): GpsCoord[] {
  if (polyline.length < 2) {
    return polyline;
  }

  const normalized = ((startFraction % 1) + 1) % 1;
  if (normalized <= 0.001 || normalized >= 0.999) {
    return polyline;
  }

  const resampled = resamplePolylineByFraction(polyline, Math.max(240, polyline.length * 2));
  const pivot = Math.round(normalized * (resampled.length - 1));
  return [...resampled.slice(pivot), ...resampled.slice(0, pivot)];
}

export function buildStartLineAtFraction(polyline: GpsCoord[], fraction: number, widthM = 24): StartLineSuggestion {
  const resampled = resamplePolylineByFraction(polyline, 240);
  const index = Math.min(
    resampled.length - 1,
    Math.max(0, Math.round(fraction * (resampled.length - 1))),
  );
  const center = resampled[index]!;
  const previous = resampled[Math.max(0, index - 2)] ?? center;
  const next = resampled[Math.min(resampled.length - 1, index + 2)] ?? center;
  const trackBearing = bearingDeg(previous, next);
  const perp = (trackBearing + 90) % 360;
  const rad = (perp * Math.PI) / 180;
  const half = widthM / 2;
  const dLat = ((half * Math.cos(rad)) / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLon =
    ((half * Math.sin(rad)) / (EARTH_RADIUS_M * Math.cos((center.lat * Math.PI) / 180))) * (180 / Math.PI);

  return {
    pointA: { lat: center.lat - dLat, lon: center.lon - dLon },
    pointB: { lat: center.lat + dLat, lon: center.lon + dLon },
    confidence: 0.95,
  };
}

export function buildStartLineFromEndpoints(
  pointA: GpsCoord,
  pointB: GpsCoord,
  confidence = 0.98,
): StartLineSuggestion {
  return { pointA, pointB, confidence };
}

export function startLineCenter(startLine: StartLineSuggestion): GpsCoord {
  return {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
}

export function startFractionForStartLine(polyline: GpsCoord[], startLine: StartLineSuggestion): number {
  return findNearestPolylineFraction(polyline, startLineCenter(startLine)).fraction;
}

export function startLineLengthM(startLine: StartLineSuggestion): number {
  return haversineM(startLine.pointA, startLine.pointB);
}

function sumCurvatureWindow(smoothed: number[], center: number, radius: number) {
  let sum = 0;
  for (let index = Math.max(0, center - radius); index <= Math.min(smoothed.length - 1, center + radius); index += 1) {
    sum += smoothed[index] ?? 0;
  }
  return sum;
}

function distanceAlongPolyline(points: GpsCoord[], fromIndex: number, toIndex: number) {
  if (toIndex <= fromIndex) {
    return 0;
  }

  let distance = 0;
  for (let index = fromIndex + 1; index <= toIndex; index += 1) {
    distance += haversineM(points[index - 1]!, points[index]!);
  }
  return distance;
}

function isLocalPeak(values: number[], index: number, radius = 2) {
  const value = values[index] ?? 0;
  for (let offset = -radius; offset <= radius; offset += 1) {
    if (offset === 0) {
      continue;
    }
    if ((values[index + offset] ?? 0) > value) {
      return false;
    }
  }
  return true;
}

function smoothIndexForResampled(resampledIndex: number) {
  return Math.max(0, resampledIndex - 1);
}

function localCurvatureAt(smoothed: number[], resampledIndex: number) {
  return smoothed[Math.min(smoothed.length - 1, smoothIndexForResampled(resampledIndex))] ?? 0;
}

type TypedCornerCandidate = {
  resampledIndex: number;
  score: number;
  localCurvature: number;
  detectionProfile: DetectableCurveType;
  signedCurvature: number;
};

type ProfileSignals = {
  apex: number[];
  deflection: number[];
  signed: number[];
};

function buildProfileSignals(
  curvatures: number[],
  signedCurvatures: number[],
  profile: DetectableCurveType,
): ProfileSignals {
  const config = CURVE_DETECTION_PROFILES[profile];
  const unsignedSource = curvatures;
  const signedSource = signedCurvatures.map((value) => Math.abs(value));
  const apexSource = config.useSignedPeaks ? signedSource : unsignedSource;
  const apex = smoothValues(apexSource, config.apexSmoothWindow);
  const deflectionBase = smoothValues(apexSource, config.deflectionSmoothWindow);
  const deflection = deflectionBase.map((_, index) =>
    sumCurvatureWindow(deflectionBase, index, config.deflectionRadius),
  );

  return { apex, deflection, signed: signedCurvatures };
}

function collectProfileCandidates(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  profile: DetectableCurveType,
): TypedCornerCandidate[] {
  const config = CURVE_DETECTION_PROFILES[profile];
  const signals = buildProfileSignals(curvatures, signedCurvatures, profile);
  const { apex, deflection } = signals;
  const deflectionRadius = config.deflectionRadius;
  const candidates: TypedCornerCandidate[] = [];

  for (let i = deflectionRadius; i < apex.length - deflectionRadius; i += 1) {
    const localCurvature = apex[i] ?? 0;
    const rawCurvature = config.useSignedPeaks
      ? Math.abs(signedCurvatures[i] ?? 0)
      : curvatures[i] ?? 0;
    const deflectionScore = deflection[i] ?? 0;
    const arcTurn = sumCurvatureWindow(apex, i, config.arcTurnRadius);
    const resampledIndex = i + 1;

    const isCurvaturePeak = localCurvature >= config.minLocalCurvatureDeg && isLocalPeak(apex, i);
    const isDeflectionPeak = deflectionScore >= config.minDeflectionScore && isLocalPeak(deflection, i);
    if (!isCurvaturePeak && !isDeflectionPeak) {
      continue;
    }

    if (rawCurvature < config.minRawCurvatureDeg && localCurvature < config.minLocalCurvatureDeg + 1) {
      continue;
    }

    if (arcTurn < config.minArcTurnDeg) {
      continue;
    }

    if (localCurvature < config.minLocalCurvatureDeg + 2 && deflectionScore < config.minDeflectionScore + 6) {
      continue;
    }

    candidates.push({
      resampledIndex,
      score: deflectionScore + localCurvature * 0.65,
      localCurvature,
      detectionProfile: profile,
      signedCurvature: signedCurvatures[i] ?? 0,
    });
  }

  return candidates;
}

function collectSweeperRunCandidates(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
): TypedCornerCandidate[] {
  const config = CURVE_DETECTION_PROFILES.sweeper;
  const signals = buildProfileSignals(curvatures, signedCurvatures, 'sweeper');
  const { apex } = signals;
  const candidates: TypedCornerCandidate[] = [];
  let runStart = -1;

  for (let i = 0; i < apex.length; i += 1) {
    if ((apex[i] ?? 0) >= config.minLocalCurvatureDeg - 0.5) {
      if (runStart < 0) {
        runStart = i;
      }
    } else if (runStart >= 0) {
      if (i - runStart >= 4) {
        let best = runStart;
        for (let j = runStart; j < i; j += 1) {
          if ((apex[j] ?? 0) > (apex[best] ?? 0)) {
            best = j;
          }
        }
        const localCurvature = apex[best] ?? 0;
        const arcTurn = sumCurvatureWindow(apex, best, config.arcTurnRadius);
        if (localCurvature >= config.minLocalCurvatureDeg && arcTurn >= config.minArcTurnDeg) {
          candidates.push({
            resampledIndex: best + 1,
            score: arcTurn + localCurvature,
            localCurvature,
            detectionProfile: 'sweeper',
            signedCurvature: signedCurvatures[best] ?? 0,
          });
        }
      }
      runStart = -1;
    }
  }

  return candidates;
}

function dedupeTypedCandidates(
  resampled: GpsCoord[],
  candidates: TypedCornerCandidate[],
): TypedCornerCandidate[] {
  const sorted = [...candidates].sort((left, right) => left.resampledIndex - right.resampledIndex);
  const deduped: TypedCornerCandidate[] = [];
  const crossProfileDedupM = 14;

  for (const candidate of sorted) {
    const last = deduped.at(-1);
    if (
      last &&
      distanceAlongPolyline(resampled, last.resampledIndex, candidate.resampledIndex) < crossProfileDedupM
    ) {
      const lastPriority = PROFILE_PRIORITY[last.detectionProfile];
      const candidatePriority = PROFILE_PRIORITY[candidate.detectionProfile];
      if (
        candidatePriority > lastPriority ||
        (candidatePriority === lastPriority && candidate.score > last.score)
      ) {
        deduped[deduped.length - 1] = candidate;
      }
      continue;
    }
    deduped.push(candidate);
  }

  return deduped;
}

function isChicanePairSeparation(
  resampled: GpsCoord[],
  signedCurvatures: number[],
  fromIndex: number,
  toIndex: number,
) {
  const separation = distanceAlongPolyline(resampled, fromIndex, toIndex);
  if (separation < CHICANE_PAIR_MIN_SEPARATION_M || separation > CHICANE_PAIR_MAX_SEPARATION_M) {
    return false;
  }

  const fromSigned = signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, fromIndex - 1))] ?? 0;
  const toSigned = signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, toIndex - 1))] ?? 0;
  return (
    Math.sign(fromSigned) !== 0 &&
    Math.sign(toSigned) !== 0 &&
    Math.sign(fromSigned) !== Math.sign(toSigned)
  );
}

function selectPeaksFromTypedCandidates(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  candidates: TypedCornerCandidate[],
): {
  peakIndices: number[];
  peakScores: number[];
  peakProfiles: Map<number, DetectableCurveType>;
} {
  const peakIndices: number[] = [];
  const peakScores: number[] = [];
  const peakProfiles = new Map<number, DetectableCurveType>();

  for (const candidate of candidates) {
    const config = CURVE_DETECTION_PROFILES[candidate.detectionProfile];

    if (peakIndices.length === 0) {
      peakIndices.push(candidate.resampledIndex);
      peakScores.push(candidate.score);
      peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
      continue;
    }

    const lastIndex = peakIndices.length - 1;
    const lastPeak = peakIndices[lastIndex]!;
    const separation = distanceAlongPolyline(resampled, lastPeak, candidate.resampledIndex);
    const minSeparationM = Math.max(config.minSeparationM, GLOBAL_MIN_CORNER_SEPARATION_M);
    const oppositeSignChicane = isChicanePairSeparation(
      resampled,
      signedCurvatures,
      lastPeak,
      candidate.resampledIndex,
    );

    if (separation < minSeparationM) {
      if (oppositeSignChicane) {
        peakIndices.push(candidate.resampledIndex);
        peakScores.push(candidate.score);
        peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
        continue;
      }

      const lastProfile = peakProfiles.get(lastPeak) ?? 'sharp';
      const lastSignals = buildProfileSignals(curvatures, signedCurvatures, lastProfile);
      const candidateSignals = buildProfileSignals(curvatures, signedCurvatures, candidate.detectionProfile);
      const lastCurvature = localCurvatureAt(lastSignals.apex, lastPeak);
      const candidateCurvature = localCurvatureAt(candidateSignals.apex, candidate.resampledIndex);
      const lastRaw = curvatures[Math.max(0, Math.min(curvatures.length - 1, lastPeak - 1))] ?? 0;
      const candidateRaw = curvatures[
        Math.max(0, Math.min(curvatures.length - 1, candidate.resampledIndex - 1))
      ] ?? 0;

      if (
        candidateCurvature > lastCurvature ||
        candidateRaw > lastRaw + 1.5 ||
        candidate.score > (peakScores[lastIndex] ?? 0)
      ) {
        peakIndices[lastIndex] = candidate.resampledIndex;
        peakScores[lastIndex] = candidate.score;
        peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
      }
      continue;
    }

    peakIndices.push(candidate.resampledIndex);
    peakScores.push(candidate.score);
    peakProfiles.set(candidate.resampledIndex, candidate.detectionProfile);
  }

  return { peakIndices, peakScores, peakProfiles };
}

function measureArcLengthM(resampled: GpsCoord[], centerIndex: number, halfWindow: number) {
  const start = Math.max(0, centerIndex - halfWindow);
  const end = Math.min(resampled.length - 1, centerIndex + halfWindow);
  return distanceAlongPolyline(resampled, start, end);
}

export function classifyCurveAtPeak(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  peakIndex: number,
  prevPeakIndex?: number,
  nextPeakIndex?: number,
): CurveType {
  const smoothIndex = Math.max(0, Math.min(curvatures.length - 1, peakIndex - 1));
  const local = curvatures[smoothIndex] ?? 0;
  const signed = signedCurvatures[smoothIndex] ?? 0;
  const arcTurn = sumCurvatureWindow(smoothValues(curvatures, 5), smoothIndex, 5);
  const arcLengthM = measureArcLengthM(resampled, peakIndex, 10);

  if (prevPeakIndex !== undefined) {
    const sepPrev = distanceAlongPolyline(resampled, prevPeakIndex, peakIndex);
    const prevSigned = signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, prevPeakIndex - 1))] ?? 0;
    if (
      sepPrev >= 6 &&
      sepPrev <= 28 &&
      Math.sign(prevSigned) !== 0 &&
      Math.sign(signed) !== 0 &&
      Math.sign(prevSigned) !== Math.sign(signed)
    ) {
      return 'chicane';
    }
  }

  if (nextPeakIndex !== undefined) {
    const sepNext = distanceAlongPolyline(resampled, peakIndex, nextPeakIndex);
    const nextSigned = signedCurvatures[Math.max(0, Math.min(signedCurvatures.length - 1, nextPeakIndex - 1))] ?? 0;
    if (
      sepNext >= 6 &&
      sepNext <= 28 &&
      Math.sign(nextSigned) !== 0 &&
      Math.sign(signed) !== 0 &&
      Math.sign(nextSigned) !== Math.sign(signed)
    ) {
      return 'chicane';
    }
  }

  if (local >= 14 && arcTurn >= 42) {
    return 'hairpin';
  }

  if (arcLengthM >= 50 && local <= 9 && arcTurn >= 14) {
    return 'sweeper';
  }

  if (local < 6 && arcTurn < 10) {
    return 'kink';
  }

  if (local >= 8 || arcTurn >= 20) {
    return 'sharp';
  }

  return 'complex';
}

function consolidateCornerPeaks(
  resampled: GpsCoord[],
  curvatures: number[],
  signedCurvatures: number[],
  apexCurvature: number[],
  peakIndices: number[],
  peakScores: number[],
  peakProfiles: Map<number, DetectableCurveType>,
  minSeparationM = GLOBAL_MIN_CORNER_SEPARATION_M,
) {
  if (peakIndices.length <= 1) {
    return { peakIndices, peakScores, peakProfiles };
  }

  const scoreByIndex = new Map<number, number>();
  peakIndices.forEach((index, position) => {
    scoreByIndex.set(index, peakScores[position] ?? 0);
  });

  const sorted = [...peakIndices].sort((left, right) => left - right);
  const kept: number[] = [];

  for (const index of sorted) {
    const lastKept = kept.at(-1);
    if (lastKept === undefined) {
      kept.push(index);
      continue;
    }

    const separation = distanceAlongPolyline(resampled, lastKept, index);
    if (isChicanePairSeparation(resampled, signedCurvatures, lastKept, index)) {
      kept.push(index);
      continue;
    }

    if (separation < minSeparationM) {
      const lastScore = scoreByIndex.get(lastKept) ?? 0;
      const currentScore = scoreByIndex.get(index) ?? 0;
      const lastCurvature = localCurvatureAt(apexCurvature, lastKept);
      const currentCurvature = localCurvatureAt(apexCurvature, index);
      if (currentCurvature > lastCurvature || currentScore > lastScore) {
        peakProfiles.delete(lastKept);
        kept[kept.length - 1] = index;
        scoreByIndex.set(index, Math.max(currentScore, lastScore));
      }
      continue;
    }

    kept.push(index);
  }

  return {
    peakIndices: kept,
    peakScores: kept.map((index) => scoreByIndex.get(index) ?? 0),
    peakProfiles,
  };
}

function mergeNearbyCornerComplexes(
  resampled: GpsCoord[],
  apexCurvature: number[],
  peakIndices: number[],
  peakScores: number[],
  maxComplexSeparationM = 38,
  resampleSpacingM = 3,
) {
  if (peakIndices.length < 2) {
    return { peakIndices, peakScores };
  }

  const mergedIndices: number[] = [];
  const mergedScores: number[] = [];

  let clusterStart = 0;
  while (clusterStart < peakIndices.length) {
    let clusterEnd = clusterStart;
    while (
      clusterEnd + 1 < peakIndices.length &&
      distanceAlongPolyline(resampled, peakIndices[clusterEnd]!, peakIndices[clusterEnd + 1]!) <= maxComplexSeparationM
    ) {
      clusterEnd += 1;
    }

    if (clusterEnd === clusterStart) {
      mergedIndices.push(peakIndices[clusterStart]!);
      mergedScores.push(peakScores[clusterStart] ?? 0);
    } else {
      const clusterFrom = peakIndices[clusterStart]!;
      const clusterTo = peakIndices[clusterEnd]!;
      const searchEnd = Math.min(resampled.length - 1, clusterTo + Math.max(4, Math.ceil(18 / resampleSpacingM)));

      let bestIndex = clusterFrom;
      let bestCurvature = localCurvatureAt(apexCurvature, clusterFrom);
      let bestScore = peakScores[clusterStart] ?? 0;

      for (let index = clusterFrom; index <= searchEnd; index += 1) {
        const curvature = localCurvatureAt(apexCurvature, index);
        const score = peakScores[clusterStart] ?? 0;
        if (curvature > bestCurvature || (curvature === bestCurvature && score > bestScore)) {
          bestCurvature = curvature;
          bestIndex = index;
          bestScore = score;
        }
      }

      mergedIndices.push(bestIndex);
      mergedScores.push(bestScore);
    }

    clusterStart = clusterEnd + 1;
  }

  return { peakIndices: mergedIndices, peakScores: mergedScores };
}

function gapDistanceAlongLoop(
  resampled: GpsCoord[],
  startPeak: number,
  endPeak: number,
) {
  if (endPeak > startPeak) {
    return distanceAlongPolyline(resampled, startPeak, endPeak);
  }

  const tail = distanceAlongPolyline(resampled, startPeak, resampled.length - 1);
  const head = distanceAlongPolyline(resampled, 0, endPeak);
  return tail + head;
}

function distanceAlongLoopBetween(
  resampled: GpsCoord[],
  fromIndex: number,
  toIndex: number,
) {
  if (fromIndex === toIndex) {
    return 0;
  }

  if (fromIndex < toIndex) {
    return distanceAlongPolyline(resampled, fromIndex, toIndex);
  }

  return (
    distanceAlongPolyline(resampled, fromIndex, resampled.length - 1) +
    distanceAlongPolyline(resampled, 0, toIndex)
  );
}

function nearestLoopSeparationM(resampled: GpsCoord[], peakIndices: number[], candidateIndex: number) {
  return peakIndices.reduce((min, peak) => {
    const forward = distanceAlongLoopBetween(resampled, peak, candidateIndex);
    const backward = distanceAlongLoopBetween(resampled, candidateIndex, peak);
    return Math.min(min, forward, backward);
  }, Number.POSITIVE_INFINITY);
}

function isIndexInGap(resampledIndex: number, startPeak: number, endPeak: number) {
  if (endPeak > startPeak) {
    return resampledIndex > startPeak && resampledIndex < endPeak;
  }

  return resampledIndex > startPeak || resampledIndex < endPeak;
}

function distanceInGapFromStart(
  resampled: GpsCoord[],
  startPeak: number,
  endPeak: number,
  resampledIndex: number,
) {
  if (endPeak > startPeak) {
    return distanceAlongPolyline(resampled, startPeak, resampledIndex);
  }

  if (resampledIndex >= startPeak) {
    return distanceAlongPolyline(resampled, startPeak, resampledIndex);
  }

  return (
    distanceAlongPolyline(resampled, startPeak, resampled.length - 1) +
    distanceAlongPolyline(resampled, 0, resampledIndex)
  );
}

function distanceInGapToEnd(
  resampled: GpsCoord[],
  startPeak: number,
  endPeak: number,
  resampledIndex: number,
) {
  if (endPeak > startPeak) {
    return distanceAlongPolyline(resampled, resampledIndex, endPeak);
  }

  if (resampledIndex <= endPeak) {
    return distanceAlongPolyline(resampled, resampledIndex, endPeak);
  }

  return (
    distanceAlongPolyline(resampled, resampledIndex, resampled.length - 1) +
    distanceAlongPolyline(resampled, 0, endPeak)
  );
}

function supplementSignedCurvaturePeaks(
  resampled: GpsCoord[],
  signedCurvatures: number[],
  peakIndices: number[],
  minGapDistanceM = 38,
  minSignedDeg = 9,
  minNeighborSeparationM = 6,
  maxSupplementsPerGap = 4,
) {
  const absSigned = signedCurvatures.map((value) => Math.abs(value));
  const sortedPeaks = [...peakIndices].sort((left, right) => left - right);
  const supplemented = new Set(sortedPeaks);

  for (let peakIndex = 0; peakIndex < sortedPeaks.length; peakIndex += 1) {
    const startPeak = sortedPeaks[peakIndex]!;
    const endPeak = sortedPeaks[(peakIndex + 1) % sortedPeaks.length]!;
    const gapDistance = gapDistanceAlongLoop(resampled, startPeak, endPeak);
    if (gapDistance < minGapDistanceM) {
      continue;
    }

    const gapCandidates: Array<{ resampledIndex: number; magnitude: number }> = [];

    for (let i = 2; i < signedCurvatures.length - 2; i += 1) {
      const resampledIndex = i + 1;
      if (!isIndexInGap(resampledIndex, startPeak, endPeak)) {
        continue;
      }

      const magnitude = absSigned[i] ?? 0;
      if (magnitude < minSignedDeg || !isLocalPeak(absSigned, i)) {
        continue;
      }

      const fromStart = distanceInGapFromStart(resampled, startPeak, endPeak, resampledIndex);
      const toEnd = distanceInGapToEnd(resampled, startPeak, endPeak, resampledIndex);
      if (fromStart < minNeighborSeparationM || toEnd < minNeighborSeparationM) {
        continue;
      }

      gapCandidates.push({ resampledIndex, magnitude });
    }

    gapCandidates.sort((left, right) => right.magnitude - left.magnitude);

    if (gapCandidates.length >= 2) {
      gapCandidates.slice(0, maxSupplementsPerGap).forEach((candidate) => {
        supplemented.add(candidate.resampledIndex);
      });
    } else if (gapCandidates.length === 1 && gapCandidates[0]!.magnitude >= 7) {
      supplemented.add(gapCandidates[0]!.resampledIndex);
    }
  }

  return [...supplemented].sort((left, right) => left - right);
}

function fillLargeGapsWithApexPeaks(
  resampled: GpsCoord[],
  apexCurvature: number[],
  rawCurvatures: number[],
  peakIndices: number[],
  minGapDistanceM = 38,
  segmentSpacingM = 30,
  minApexDeg = 3.5,
  minSeparationM = 10,
  maxAdditionsPerGap = 10,
) {
  if (peakIndices.length < 2) {
    return peakIndices;
  }

  const filled = new Set(peakIndices);
  const sortedPeaks = [...peakIndices].sort((left, right) => left - right);

  for (let peakIndex = 0; peakIndex < sortedPeaks.length; peakIndex += 1) {
    const startPeak = sortedPeaks[peakIndex]!;
    const endPeak = sortedPeaks[(peakIndex + 1) % sortedPeaks.length]!;
    const gapDistance = gapDistanceAlongLoop(resampled, startPeak, endPeak);
    if (gapDistance < minGapDistanceM) {
      continue;
    }

    const segmentCount = Math.min(maxAdditionsPerGap, Math.max(1, Math.ceil(gapDistance / segmentSpacingM)));
    let addedInGap = 0;

    for (let segment = 1; segment <= segmentCount; segment += 1) {
      const segStartDist = (gapDistance * (segment - 1)) / segmentCount;
      const segEndDist = (gapDistance * segment) / segmentCount;

      let bestIndex = -1;
      let bestCurvature = 0;

      for (let i = 0; i < apexCurvature.length; i += 1) {
        const resampledIndex = i + 1;
        if (!isIndexInGap(resampledIndex, startPeak, endPeak)) {
          continue;
        }

        const fromStart = distanceInGapFromStart(resampled, startPeak, endPeak, resampledIndex);
        if (fromStart < segStartDist || fromStart >= segEndDist) {
          continue;
        }

        const curvature = Math.max(apexCurvature[i] ?? 0, rawCurvatures[i] ?? 0);
        if (curvature > bestCurvature) {
          bestCurvature = curvature;
          bestIndex = resampledIndex;
        }
      }

      const threshold = gapDistance > 70 ? 2.5 : minApexDeg;
      if (bestIndex < 0 || bestCurvature < threshold) {
        continue;
      }

      const nearestSeparation = nearestLoopSeparationM(resampled, [...filled], bestIndex);
      if (nearestSeparation < minSeparationM) {
        continue;
      }

      filled.add(bestIndex);
      addedInGap += 1;
    }

    // Large gaps that are mostly straight still need at least one marker.
    if (addedInGap === 0 && gapDistance > 65) {
      let bestIndex = -1;
      let bestCurvature = 0;

      for (let i = 0; i < apexCurvature.length; i += 1) {
        const resampledIndex = i + 1;
        if (!isIndexInGap(resampledIndex, startPeak, endPeak)) {
          continue;
        }

        const curvature = Math.max(apexCurvature[i] ?? 0, rawCurvatures[i] ?? 0);
        if (curvature > bestCurvature) {
          bestCurvature = curvature;
          bestIndex = resampledIndex;
        }
      }

      if (bestIndex >= 0 && bestCurvature >= 2) {
        const nearestSeparation = nearestLoopSeparationM(resampled, [...filled], bestIndex);
        if (nearestSeparation >= minSeparationM) {
          filled.add(bestIndex);
        }
      }
    }
  }

  return [...filled].sort((left, right) => left - right);
}

export function detectCornersFromPolyline(
  polyline: GpsCoord[],
  speedByIndex?: number[],
  options: CornerDetectionOptions = {},
): CornerApexSuggestion[] {
  // 2 m spacing — balance resolution vs. cluster noise.
  const resampleSpacingM = 2;
  const resampled = resamplePolylineByDistance(polyline, resampleSpacingM);
  if (resampled.length < 12) {
    return [];
  }

  const curvatures: number[] = [];
  for (let i = 1; i < resampled.length - 1; i += 1) {
    curvatures.push(curvatureDeg(resampled[i - 1]!, resampled[i]!, resampled[i + 1]!));
  }

  const signedCurvatures: number[] = [];
  for (let i = 1; i < resampled.length - 1; i += 1) {
    signedCurvatures.push(signedCurvatureDeg(resampled[i - 1]!, resampled[i]!, resampled[i + 1]!));
  }

  // Default sharp profile signals — used for merge, gap fill, and apex refinement.
  const sharpSignals = buildProfileSignals(curvatures, signedCurvatures, 'sharp');
  const { apex: apexCurvature, deflection } = sharpSignals;
  const smoothed = smoothValues(curvatures, CURVE_DETECTION_PROFILES.sharp.deflectionSmoothWindow);
  const kerbCenterIndices = (options.kerbLines ?? [])
    .filter((line) => line.length > 0)
    .map((line) => line[Math.floor(line.length / 2)]!)
    .map((kerbCenter) => resampled.reduce((bestIndex, point, index) =>
      haversineM(point, kerbCenter) < haversineM(resampled[bestIndex]!, kerbCenter) ? index : bestIndex,
    0));
  const kerbEvidence = resampled.map((_, index) => {
    const nearestKerbDistanceM = kerbCenterIndices.reduce((nearest, kerbIndex) => {
      const sampleDistance = Math.abs(index - kerbIndex) * resampleSpacingM;
      const wrappedDistance = (resampled.length - Math.abs(index - kerbIndex)) * resampleSpacingM;
      return Math.min(nearest, sampleDistance, wrappedDistance);
    }, Number.POSITIVE_INFINITY);
    return Number.isFinite(nearestKerbDistanceM)
      ? cornerKerbEvidenceScore(nearestKerbDistanceM)
      : 0;
  });
  const scoreAtPeak = (peakIndex: number) => {
    const signalIndex = Math.max(0, Math.min(apexCurvature.length - 1, peakIndex - 1));
    return (deflection[signalIndex] ?? 0)
      + (apexCurvature[signalIndex] ?? 0) * 0.65
      + (kerbEvidence[peakIndex] ?? 0);
  };

  const typedCandidates: TypedCornerCandidate[] = [];
  for (const profile of DETECTION_PROFILE_ORDER) {
    typedCandidates.push(...collectProfileCandidates(resampled, curvatures, signedCurvatures, profile));
  }

  const dedupedCandidates = dedupeTypedCandidates(resampled, typedCandidates);
  const selected = selectPeaksFromTypedCandidates(resampled, curvatures, signedCurvatures, dedupedCandidates);
  let peakIndices = [...selected.peakIndices];
  let peakScores = [...selected.peakScores];
  const peakProfiles = new Map(selected.peakProfiles);

  const chicanePeaks = peakIndices.filter((index) => peakProfiles.get(index) === 'chicane');
  const mergeablePeaks = peakIndices.filter((index) => peakProfiles.get(index) !== 'chicane');
  const mergeableScores = mergeablePeaks.map((index) => {
    const position = peakIndices.indexOf(index);
    return peakScores[position] ?? 0;
  });

  const merged = mergeNearbyCornerComplexes(
    resampled,
    apexCurvature,
    mergeablePeaks,
    mergeableScores,
    32,
    resampleSpacingM,
  );
  const mergedPeakSet = new Set(merged.peakIndices);

  peakIndices = [...merged.peakIndices, ...chicanePeaks].sort((left, right) => left - right);
  peakScores = peakIndices.map(scoreAtPeak);

  const supplementedPeakIndices = fillLargeGapsWithApexPeaks(
    resampled,
    apexCurvature,
    curvatures,
    supplementSignedCurvaturePeaks(resampled, signedCurvatures, peakIndices, 45, 8, 7, 3),
    48,
    36,
    5,
    10,
    4,
  );
  peakScores.length = 0;
  for (const peakIndex of supplementedPeakIndices) {
    if (!peakProfiles.has(peakIndex)) {
      peakProfiles.set(peakIndex, 'sharp');
    }
    peakScores.push(scoreAtPeak(peakIndex));
  }
  peakIndices = [...supplementedPeakIndices];

  for (const kerbIndex of kerbCenterIndices) {
    const radiusSamples = Math.ceil(14 / resampleSpacingM);
    let bestIndex = kerbIndex;
    let bestCurvature = 0;
    for (let offset = -radiusSamples; offset <= radiusSamples; offset += 1) {
      const index = (kerbIndex + offset + resampled.length) % resampled.length;
      const curvature = apexCurvature[Math.max(0, Math.min(apexCurvature.length - 1, index - 1))] ?? 0;
      if (curvature > bestCurvature) {
        bestCurvature = curvature;
        bestIndex = index;
      }
    }
    const nearestPeakM = peakIndices.reduce(
      (nearest, peakIndex) => Math.min(nearest, Math.abs(peakIndex - bestIndex) * resampleSpacingM),
      Number.POSITIVE_INFINITY,
    );
    if (bestCurvature >= 2.5 && nearestPeakM >= GLOBAL_MIN_CORNER_SEPARATION_M) {
      peakIndices.push(bestIndex);
      peakScores.push(scoreAtPeak(bestIndex));
      peakProfiles.set(bestIndex, 'sharp');
    }
  }

  const consolidated = consolidateCornerPeaks(
    resampled,
    curvatures,
    signedCurvatures,
    apexCurvature,
    peakIndices,
    peakScores,
    peakProfiles,
  );
  peakIndices = consolidated.peakIndices;
  peakScores = consolidated.peakScores;

  // One sweeper apex per long constant-curvature run that gap passes may miss.
  for (const sweeperCandidate of collectSweeperRunCandidates(resampled, curvatures, signedCurvatures)) {
    const nearest = peakIndices.reduce((min, peak) => {
      return Math.min(min, distanceAlongPolyline(resampled, peak, sweeperCandidate.resampledIndex));
    }, Number.POSITIVE_INFINITY);
    if (nearest >= 45) {
      peakIndices.push(sweeperCandidate.resampledIndex);
      peakProfiles.set(sweeperCandidate.resampledIndex, 'sweeper');
      peakScores.push(scoreAtPeak(sweeperCandidate.resampledIndex));
    }
  }

  const resort = peakIndices
    .map((index, position) => ({ index, score: peakScores[position] ?? 0 }))
    .sort((left, right) => left.index - right.index);
  peakIndices = resort.map((entry) => entry.index);
  peakScores = resort.map((entry) => entry.score);

  const finalConsolidated = consolidateCornerPeaks(
    resampled,
    curvatures,
    signedCurvatures,
    apexCurvature,
    peakIndices,
    peakScores,
    peakProfiles,
    GLOBAL_MIN_CORNER_SEPARATION_M,
  );
  peakIndices = finalConsolidated.peakIndices;
  peakScores = finalConsolidated.peakScores;

  return peakIndices.map((peakIndex, cornerIndex) => {
    let apexIndex = peakIndex;
    const detectionProfile = peakProfiles.get(peakIndex) ?? 'sharp';
    const profileConfig = CURVE_DETECTION_PROFILES[detectionProfile];
    const profileSignals = buildProfileSignals(curvatures, signedCurvatures, detectionProfile);
    const smoothIndex = Math.max(0, Math.min(smoothed.length - 1, peakIndex - 1));
    const searchRadius = Math.max(4, Math.ceil(profileConfig.apexSmoothWindow * 1.5));
    const searchStart = Math.max(0, smoothIndex - searchRadius);
    const searchEnd = Math.min(smoothed.length - 1, smoothIndex + searchRadius);
    let bestCurvature = -1;
    const useDetectedApex = !mergedPeakSet.has(peakIndex);

    if (speedByIndex?.length) {
      const speedSearchStart = Math.max(0, peakIndex - searchRadius);
      const speedSearchEnd = Math.min(resampled.length - 1, peakIndex + searchRadius);
      let minSpeed = Number.POSITIVE_INFINITY;
      for (let index = speedSearchStart; index <= speedSearchEnd; index += 1) {
        const speed = speedByIndex[Math.min(speedByIndex.length - 1, index)] ?? Number.POSITIVE_INFINITY;
        if (speed < minSpeed) {
          minSpeed = speed;
          apexIndex = index;
        }
      }
    } else if (useDetectedApex) {
      apexIndex = peakIndex;
    } else {
      for (let index = searchStart; index <= searchEnd; index += 1) {
        const value = profileSignals.apex[index] ?? 0;
        if (value > bestCurvature) {
          bestCurvature = value;
          apexIndex = index + 1;
        }
      }
    }

    const apex = resampled[apexIndex]!;
    const curvature =
      profileSignals.apex[Math.max(0, Math.min(profileSignals.apex.length - 1, apexIndex - 1))] ??
      profileConfig.minLocalCurvatureDeg;
    const prevPeakIndex = cornerIndex > 0 ? peakIndices[cornerIndex - 1] : undefined;
    const nextPeakIndex = cornerIndex < peakIndices.length - 1 ? peakIndices[cornerIndex + 1] : undefined;
    const curveType = classifyCurveAtPeak(
      resampled,
      curvatures,
      signedCurvatures,
      peakIndex,
      prevPeakIndex,
      nextPeakIndex,
    );
    const turnNumber = cornerIndex + 1;
    return {
      id: `corner-${turnNumber}`,
      label: `Turn ${turnNumber}`,
      lat: apex.lat,
      lon: apex.lon,
      turnNumber,
      confidence: Math.min(0.98, 0.58 + curvature / 110 + (peakScores[cornerIndex] ?? 0) / 200),
      curveType,
      detectionProfile,
    };
  });
}

export function sortCornersAlongPolyline(
  corners: CornerApexSuggestion[],
  polyline: GpsCoord[],
): CornerApexSuggestion[] {
  return [...corners].sort((left, right) => {
    const leftFraction = findNearestPolylineFraction(polyline, left).fraction;
    const rightFraction = findNearestPolylineFraction(polyline, right).fraction;
    return leftFraction - rightFraction;
  });
}

export function renumberCorners(corners: CornerApexSuggestion[]): CornerApexSuggestion[] {
  return corners.map((corner, index) => ({
    ...corner,
    turnNumber: index + 1,
    id: `corner-${index + 1}`,
    label: `Turn ${index + 1}`,
  }));
}

export function snapCoordToPolyline(polyline: GpsCoord[], coord: GpsCoord): GpsCoord {
  return findNearestPolylineFraction(polyline, coord).coord;
}

function bearingFromCoord(from: GpsCoord, to: GpsCoord) {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLon = ((to.lon - from.lon) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export function cornerMapOrigin(
  corners: CornerApexSuggestion[],
  startLine: StartLineSuggestion | null,
  fallback: GpsCoord,
): GpsCoord {
  if (startLine) {
    return {
      lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
      lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
    };
  }

  if (corners.length > 0) {
    return {
      lat: corners.reduce((sum, corner) => sum + corner.lat, 0) / corners.length,
      lon: corners.reduce((sum, corner) => sum + corner.lon, 0) / corners.length,
    };
  }

  return fallback;
}

export function sortCornersByBearing(
  corners: CornerApexSuggestion[],
  origin: GpsCoord,
): CornerApexSuggestion[] {
  return [...corners].sort((left, right) => {
    const leftBearing = bearingFromCoord(origin, { lat: left.lat, lon: left.lon });
    const rightBearing = bearingFromCoord(origin, { lat: right.lat, lon: right.lon });
    return leftBearing - rightBearing;
  });
}

export function moveCornerToCoord(
  corners: CornerApexSuggestion[],
  cornerId: string,
  coord: GpsCoord,
  polyline: GpsCoord[],
  options?: { snapToPolyline?: boolean },
): CornerApexSuggestion[] {
  const snap = options?.snapToPolyline !== false && polyline.length >= 2;
  const placed = snap ? snapCoordToPolyline(polyline, coord) : coord;
  const updated = corners.map((corner) =>
    corner.id === cornerId ? { ...corner, lat: placed.lat, lon: placed.lon, confidence: 0.9 } : corner,
  );
  const origin = cornerMapOrigin(updated, null, placed);
  const sorted = snap
    ? sortCornersAlongPolyline(updated, polyline)
    : sortCornersByBearing(updated, origin);
  return renumberCorners(sorted);
}

export function insertCornerAtCoord(
  corners: CornerApexSuggestion[],
  coord: GpsCoord,
  polyline: GpsCoord[],
  options?: { snapToPolyline?: boolean },
): CornerApexSuggestion[] {
  const snap = options?.snapToPolyline !== false && polyline.length >= 2;
  const placed = snap ? snapCoordToPolyline(polyline, coord) : coord;
  const newCorner: CornerApexSuggestion = {
    id: `corner-manual-${Date.now()}`,
    label: 'Turn',
    lat: placed.lat,
    lon: placed.lon,
    turnNumber: corners.length + 1,
    confidence: snap ? 0.75 : 0.65,
    curveType: 'unknown',
  };
  const origin = cornerMapOrigin(corners, null, placed);
  const sorted = snap
    ? sortCornersAlongPolyline([...corners, newCorner], polyline)
    : sortCornersByBearing([...corners, newCorner], origin);
  return renumberCorners(sorted);
}

export function buildManualLayoutPreviewPolyline(
  corners: CornerApexSuggestion[],
  startLine: StartLineSuggestion | null,
  mapCenter: GpsCoord,
): GpsCoord[] {
  const origin = cornerMapOrigin(corners, startLine, mapCenter);
  const ordered = sortCornersByBearing(corners, origin);
  if (ordered.length === 0) {
    if (!startLine) {
      return [];
    }

    return [startLine.pointA, startLine.pointB];
  }

  const vertices: GpsCoord[] = [];
  if (startLine) {
    vertices.push({
      lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
      lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
    });
  }

  for (const corner of ordered) {
    vertices.push({ lat: corner.lat, lon: corner.lon });
  }

  if (vertices.length >= 2) {
    vertices.push(vertices[0]!);
  }

  return vertices;
}

export const MANUAL_LAP_DRAW_CLOSE_THRESHOLD_M = 18;
export const MANUAL_LAP_DRAW_MIN_POINTS = 3;

export function manualLapDrawLengthM(polyline: GpsCoord[]): number {
  if (polyline.length < 2) {
    return 0;
  }

  let total = 0;
  for (let index = 1; index < polyline.length; index += 1) {
    total += haversineM(polyline[index - 1]!, polyline[index]!);
  }
  return total;
}

export function isManualLapDrawClosed(polyline: GpsCoord[]): boolean {
  if (polyline.length < 2) {
    return false;
  }

  const first = polyline[0]!;
  const last = polyline.at(-1)!;
  return haversineM(first, last) <= MANUAL_LAP_DRAW_CLOSE_THRESHOLD_M;
}

export function isManualLapDrawComplete(polyline: GpsCoord[], closed: boolean): boolean {
  if (!closed || polyline.length < MANUAL_LAP_DRAW_MIN_POINTS) {
    return false;
  }

  const uniquePoints = isManualLapDrawClosed(polyline) ? polyline.slice(0, -1) : polyline;
  return uniquePoints.length >= MANUAL_LAP_DRAW_MIN_POINTS && manualLapDrawLengthM(uniquePoints) >= 40;
}

export function closeManualLapDrawPolyline(polyline: GpsCoord[]): GpsCoord[] {
  if (polyline.length < 2) {
    return polyline;
  }

  const first = polyline[0]!;
  const last = polyline.at(-1)!;
  if (first.lat === last.lat && first.lon === last.lon) {
    return polyline;
  }

  if (isManualLapDrawClosed(polyline)) {
    return [...polyline, first];
  }

  return [...polyline, first];
}

/** Flip lap travel direction while preserving closure when present. */
export function reverseManualLapDrawPolyline(polyline: GpsCoord[]): GpsCoord[] {
  if (polyline.length < 2) {
    return polyline;
  }

  return [...polyline].reverse();
}

/** Swap CW/CCW labels when reversing a saved layout name. */
export function swapLayoutDirectionInName(layoutName: string): string {
  const name = layoutName.trim() || 'Main layout';
  if (/\bCW\b/.test(name)) {
    return name.replace(/\bCW\b/, 'CCW');
  }
  if (/\bCCW\b/.test(name)) {
    return name.replace(/\bCCW\b/, 'CW');
  }
  if (/clockwise/i.test(name) && !/counter/i.test(name)) {
    return name.replace(/clockwise/i, 'counter-clockwise');
  }
  if (/counter-?clockwise/i.test(name)) {
    return name.replace(/counter-?clockwise/i, 'clockwise');
  }
  if (/\(reversed\)$/i.test(name)) {
    return name.replace(/\s*\(reversed\)$/i, '').trim();
  }
  return `${name} (reversed)`;
}

export function appendManualLapDrawPoint(
  polyline: GpsCoord[],
  coord: GpsCoord,
  options?: { closeThresholdM?: number },
): { polyline: GpsCoord[]; closed: boolean } {
  const threshold = options?.closeThresholdM ?? MANUAL_LAP_DRAW_CLOSE_THRESHOLD_M;
  if (polyline.length >= MANUAL_LAP_DRAW_MIN_POINTS && haversineM(coord, polyline[0]!) <= threshold) {
    return { polyline: closeManualLapDrawPolyline(polyline), closed: true };
  }

  return { polyline: [...polyline, coord], closed: false };
}

/** Append a freehand stroke point, skipping samples closer than minSpacingM. */
export function appendManualLapDrawStrokePoint(
  polyline: GpsCoord[],
  coord: GpsCoord,
  options?: { minSpacingM?: number; closeThresholdM?: number },
): { polyline: GpsCoord[]; closed: boolean; appended: boolean } {
  const minSpacing = options?.minSpacingM ?? 3;
  const last = polyline.at(-1);
  if (last && haversineM(last, coord) < minSpacing) {
    return { polyline, closed: false, appended: false };
  }

  const next = appendManualLapDrawPoint(polyline, coord, options);
  return { ...next, appended: true };
}

export function undoManualLapDrawPoint(polyline: GpsCoord[]): GpsCoord[] {
  if (polyline.length === 0) {
    return polyline;
  }

  const next = polyline.slice(0, -1);
  if (next.length >= 2 && isManualLapDrawClosed(next)) {
    return next.slice(0, -1);
  }

  return next;
}

export function removeManualLapDrawPointAtIndex(polyline: GpsCoord[], index: number): GpsCoord[] {
  if (index < 0 || index >= polyline.length) {
    return polyline;
  }

  const openPolyline =
    polyline.length >= 2 && isManualLapDrawClosed(polyline) ? polyline.slice(0, -1) : polyline;

  if (index >= openPolyline.length) {
    return polyline;
  }

  return openPolyline.filter((_, pointIndex) => pointIndex !== index);
}

export function reopenManualLapDrawPolyline(polyline: GpsCoord[]): GpsCoord[] {
  if (polyline.length >= 2 && isManualLapDrawClosed(polyline)) {
    return polyline.slice(0, -1);
  }

  return polyline;
}

export function buildPolylineFromManualLayout(
  corners: CornerApexSuggestion[],
  startLine: StartLineSuggestion | null,
  mapCenter: GpsCoord,
): GpsCoord[] {
  const origin = cornerMapOrigin(corners, startLine, mapCenter);
  const ordered = sortCornersByBearing(corners, origin);
  const vertices: GpsCoord[] = [];

  if (startLine) {
    vertices.push(startLine.pointA, startLine.pointB);
  }

  for (const corner of ordered) {
    vertices.push({ lat: corner.lat, lon: corner.lon });
  }

  if (vertices.length >= 2) {
    const first = vertices[0]!;
    const last = vertices.at(-1)!;
    if (haversineM(first, last) > 3) {
      vertices.push(first);
    }
    return healCircuitPolylineForDisplay(vertices);
  }

  if (ordered.length >= 2) {
    const loop = ordered.map((corner) => ({ lat: corner.lat, lon: corner.lon }));
    loop.push(loop[0]!);
    return healCircuitPolylineForDisplay(loop);
  }

  return [
    mapCenter,
    { lat: mapCenter.lat + 0.00008, lon: mapCenter.lon + 0.00008 },
  ];
}

export function buildManualTrackGeometry(input: {
  mapCenter: GpsCoord;
  startLine: StartLineSuggestion | null;
  corners: CornerApexSuggestion[];
  includeSectorMarkers?: boolean;
  drawnPolyline?: GpsCoord[] | null;
  drawnPolylineClosed?: boolean;
}): RecalibratedTrackGeometry {
  const drawnPolyline = input.drawnPolyline ?? null;
  const drawnClosed =
    Boolean(input.drawnPolylineClosed)
    && Boolean(drawnPolyline)
    && isManualLapDrawComplete(drawnPolyline!, true);

  let polyline: GpsCoord[];
  if (drawnClosed && drawnPolyline) {
    polyline = healCircuitPolylineForDisplay(drawnPolyline);
  } else if (drawnPolyline && drawnPolyline.length >= 2) {
    polyline = drawnPolyline;
  } else {
    polyline = buildPolylineFromManualLayout(input.corners, input.startLine, input.mapCenter);
  }

  let startLine =
    input.startLine ??
    (polyline.length >= 2
      ? buildStartLineAtFraction(polyline, 0)
      : {
          pointA: { lat: input.mapCenter.lat + 0.00003, lon: input.mapCenter.lon },
          pointB: { lat: input.mapCenter.lat - 0.00003, lon: input.mapCenter.lon },
          confidence: 0.5,
        });

  let startFraction = 0;
  if (drawnClosed && drawnPolyline && input.startLine) {
    const healed = healCircuitPolylineForDisplay(drawnPolyline);
    startFraction = startFractionForStartLine(healed, input.startLine);
    polyline = healCircuitPolylineForDisplay(rotatePolylineToStart(drawnPolyline, startFraction));
    startLine = input.startLine;
  }

  return {
    startFraction,
    polyline,
    startLineSuggestion: startLine,
    cornerApexSuggestions: input.corners,
    segmentSuggestions:
      input.includeSectorMarkers === false || !drawnClosed
        ? []
        : detectSegmentsFromPolyline(polyline),
    direction: polyline.length >= 3 ? detectDirection(polyline) : 'clockwise',
    lapLengthEstimateM: polylineLengthM(polyline),
  };
}

export const MANUAL_MAP_SESSION_KEY_PREFIX = 'map-layout:';

export function isManualMapSessionKey(sessionKey: string | null | undefined) {
  return Boolean(sessionKey?.startsWith(MANUAL_MAP_SESSION_KEY_PREFIX));
}

export function removeCornerById(
  corners: CornerApexSuggestion[],
  cornerId: string,
): CornerApexSuggestion[] {
  return renumberCorners(corners.filter((corner) => corner.id !== cornerId));
}

export function detectSegmentsFromPolyline(polyline: GpsCoord[]): SegmentSuggestion[] {
  const resampled = resamplePolylineByDistance(polyline, 8);
  if (resampled.length < 6) {
    return [
      { id: 's1', label: 'Sector 1', startFraction: 0, endFraction: 1 / 3, confidence: 0.7 },
      { id: 's2', label: 'Sector 2', startFraction: 1 / 3, endFraction: 2 / 3, confidence: 0.7 },
      { id: 's3', label: 'Sector 3', startFraction: 2 / 3, endFraction: 1, confidence: 0.7 },
    ];
  }

  const cumulative = [0];
  for (let i = 1; i < resampled.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + haversineM(resampled[i - 1]!, resampled[i]!));
  }
  const total = cumulative.at(-1) ?? 1;

  return [
    { id: 's1', label: 'Sector 1', startFraction: 0, endFraction: 1 / 3, confidence: 0.88 },
    { id: 's2', label: 'Sector 2', startFraction: 1 / 3, endFraction: 2 / 3, confidence: 0.86 },
    { id: 's3', label: 'Sector 3', startFraction: 2 / 3, endFraction: 1, confidence: 0.87 },
  ].map((segment) => segment);
}

export type RecalibratedTrackGeometry = {
  startFraction: number;
  polyline: GpsCoord[];
  startLineSuggestion: StartLineSuggestion;
  cornerApexSuggestions: CornerApexSuggestion[];
  segmentSuggestions: SegmentSuggestion[];
  direction: 'clockwise' | 'counterclockwise';
  lapLengthEstimateM: number;
};

export function recalibrateTrackGeometry(
  sourcePolyline: GpsCoord[],
  startFraction: number,
  options?: { startLineSuggestion?: StartLineSuggestion },
): RecalibratedTrackGeometry {
  const polyline = healCircuitPolylineForDisplay(rotatePolylineToStart(sourcePolyline, startFraction));
  const startLineSuggestion =
    options?.startLineSuggestion ?? buildStartLineAtFraction(sourcePolyline, startFraction);
  const cornerApexSuggestions = detectCornersFromPolyline(polyline);
  const segmentSuggestions = detectSegmentsFromPolyline(polyline);

  return {
    startFraction,
    polyline,
    startLineSuggestion,
    cornerApexSuggestions,
    segmentSuggestions,
    direction: detectDirection(polyline),
    lapLengthEstimateM: polylineLengthM(polyline),
  };
}

export function buildContributionDraftPayload(
  trackId: string,
  geometry: RecalibratedTrackGeometry,
  corners: CornerApexSuggestion[],
  confidenceScore: number,
): TrackContributionPayload {
  return {
    trackId,
    gpsPolyline: geometry.polyline,
    startLineSuggestion: geometry.startLineSuggestion,
    segmentSuggestions: geometry.segmentSuggestions,
    cornerApexSuggestions: corners,
    direction: geometry.direction,
    lapLengthEstimateM: geometry.lapLengthEstimateM,
    confidenceScore,
    startFraction: geometry.startFraction,
  };
}

/** Ignore stub/partial drafts (e.g. API test data with 1 corner) that would wipe auto-detection. */
export function resolveDraftCornerRestore(
  draftCorners: CornerApexSuggestion[] | undefined | null,
  autoCorners: CornerApexSuggestion[],
): {
  manualCorners: CornerApexSuggestion[] | null;
  skippedPartialDraft: boolean;
  draftCornerCount: number;
} {
  const draft = draftCorners ?? [];
  if (draft.length === 0) {
    return { manualCorners: null, skippedPartialDraft: false, draftCornerCount: 0 };
  }

  const minAccept = Math.max(3, Math.ceil(autoCorners.length * 0.25));
  if (draft.length >= minAccept) {
    return { manualCorners: draft, skippedPartialDraft: false, draftCornerCount: draft.length };
  }

  return { manualCorners: null, skippedPartialDraft: true, draftCornerCount: draft.length };
}

function resamplePolylineByFraction(points: GpsCoord[], samples: number): GpsCoord[] {
  if (points.length < 2) {
    return points;
  }

  const cumulative = [0];
  for (let i = 1; i < points.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + haversineM(points[i - 1]!, points[i]!));
  }

  const total = cumulative.at(-1) ?? 0;
  if (total <= 0) {
    return points;
  }

  const result: GpsCoord[] = [];
  let cursor = 0;
  for (let s = 0; s < samples; s += 1) {
    const target = (s / (samples - 1)) * total;
    while (cursor < cumulative.length - 2 && cumulative[cursor + 1]! < target) {
      cursor += 1;
    }
    const start = cumulative[cursor]!;
    const end = cumulative[cursor + 1]!;
    const t = end > start ? (target - start) / (end - start) : 0;
    const a = points[cursor]!;
    const b = points[cursor + 1]!;
    result.push({
      lat: a.lat + (b.lat - a.lat) * t,
      lon: a.lon + (b.lon - a.lon) * t,
    });
  }

  return result;
}

function polylineLengthM(points: GpsCoord[]) {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += haversineM(points[i - 1]!, points[i]!);
  }
  return total;
}

export function detectDirection(points: GpsCoord[]): 'clockwise' | 'counterclockwise' {
  if (points.length < 3) {
    return 'clockwise';
  }

  const origin = points[0]!;
  const latScale = 111_320;
  const lonScale = latScale * Math.cos((origin.lat * Math.PI) / 180);

  let signedArea = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    const ax = (a.lon - origin.lon) * lonScale;
    const ay = (a.lat - origin.lat) * latScale;
    const bx = (b.lon - origin.lon) * lonScale;
    const by = (b.lat - origin.lat) * latScale;
    signedArea += ax * by - bx * ay;
  }

  return signedArea > 0 ? 'counterclockwise' : 'clockwise';
}

function gpsToCoords(gps: MyChronGpsPoint[]): GpsCoord[] {
  return gps.map((point) => ({ lat: point.lat, lon: point.lon }));
}

function mapPointsToCoords(points: TrackMapPoint[]): GpsCoord[] {
  return points.map((point) => ({ lat: point.lat, lon: point.lon }));
}

function offsetCoord(coord: GpsCoord, metersEast: number, metersNorth: number): GpsCoord {
  const latOffset = metersNorth / EARTH_RADIUS_M;
  const lonOffset = metersEast / (EARTH_RADIUS_M * Math.cos((coord.lat * Math.PI) / 180));
  return {
    lat: coord.lat + (latOffset * 180) / Math.PI,
    lon: coord.lon + (lonOffset * 180) / Math.PI,
  };
}

function perpOffsetStartLine(line: StartLineSuggestion, meters: number): StartLineSuggestion {
  const mid = {
    lat: (line.pointA.lat + line.pointB.lat) / 2,
    lon: (line.pointA.lon + line.pointB.lon) / 2,
  };
  const bearing = bearingDeg(line.pointA, line.pointB);
  const perpBearing = (bearing + 90) % 360;
  const rad = (perpBearing * Math.PI) / 180;
  const dLat = (meters * Math.cos(rad)) / EARTH_RADIUS_M;
  const dLon = (meters * Math.sin(rad)) / (EARTH_RADIUS_M * Math.cos((mid.lat * Math.PI) / 180));
  const shift = { lat: (dLat * 180) / Math.PI, lon: (dLon * 180) / Math.PI };
  return {
    pointA: { lat: line.pointA.lat + shift.lat, lon: line.pointA.lon + shift.lon },
    pointB: { lat: line.pointB.lat + shift.lat, lon: line.pointB.lon + shift.lon },
    confidence: Math.max(0.4, line.confidence - 0.15),
  };
}

// ── Quality scoring ──────────────────────────────────────────────────────────

function scoreGpsQuality(gps: MyChronGpsPoint[]): number {
  if (gps.length < 10) {
    return 0;
  }

  let jumpPenalty = 0;
  let fixScore = 0;
  for (let i = 1; i < gps.length; i += 1) {
    const dist = haversineM(gps[i - 1]!, gps[i]!);
    if (dist > 80) {
      jumpPenalty += 1;
    }
    const fix = gps[i]!.fix ?? 3;
    const sats = gps[i]!.satellites ?? 8;
    fixScore += Math.min(1, fix / 3) * Math.min(1, sats / 10);
  }

  const jumpRatio = jumpPenalty / (gps.length - 1);
  const avgFix = fixScore / (gps.length - 1);
  return Math.max(0, Math.min(1, avgFix * (1 - jumpRatio * 4)));
}

function scoreLapCompleteness(gps: MyChronGpsPoint[], durationMs: number): number {
  const lengthM = polylineLengthM(gpsToCoords(gps));
  const durationOk = durationMs >= 15_000 && durationMs <= 120_000 ? 1 : 0.3;
  const closure = gps.length >= 2 ? Math.max(0, 1 - haversineM(gps[0]!, gps.at(-1)!) / 120) : 0;
  const lengthOk = lengthM > 400 && lengthM < 2500 ? 1 : 0.4;
  return durationOk * 0.4 + closure * 0.35 + lengthOk * 0.25;
}

function scoreSampleDensity(gps: MyChronGpsPoint[], durationMs: number): number {
  const samplesPerSec = gps.length / Math.max(1, durationMs / 1000);
  return Math.max(0, Math.min(1, samplesPerSec / 5));
}

function scoreSpeedConsistency(speedsKph: number[]): number {
  if (speedsKph.length < 5) {
    return 0;
  }
  const mean = speedsKph.reduce((sum, v) => sum + v, 0) / speedsKph.length;
  const variance = speedsKph.reduce((sum, v) => sum + (v - mean) ** 2, 0) / speedsKph.length;
  const cv = mean > 0 ? Math.sqrt(variance) / mean : 1;
  return Math.max(0, Math.min(1, 1 - cv * 0.8));
}

function scoreLineConsistency(reference: GpsCoord[], candidate: GpsCoord[]): number {
  const ref = resamplePolylineByFraction(reference, 64);
  const cand = resamplePolylineByFraction(candidate, 64);
  if (ref.length !== cand.length) {
    return 0;
  }

  const distances = ref.map((point, index) => haversineM(point, cand[index]!));
  const mean = distances.reduce((sum, v) => sum + v, 0) / distances.length;
  return Math.max(0, Math.min(1, 1 - mean / 25));
}

const CIRCUIT_MATCH_MIN_CONFIDENCE = 0.58;
const CIRCUIT_MATCH_MIN_DRAFT_POINTS = 16;

export function extractSessionReferencePolyline(
  session: MyChronSessionExport,
  maxPoints = 120,
): GpsCoord[] | null {
  const polylines = extractSessionReferencePolylinesForMatching(session, maxPoints);
  return polylines[0] ?? null;
}

function lapPolylineForMatching(session: MyChronSessionExport, lapNumber: number, maxPoints: number) {
  const analysis = analyzeLap(session, lapNumber);
  if (!analysis || analysis.mapPoints.length < 12) {
    return null;
  }

  const coords = downsampleTrackPoints(mapPointsToCoords(analysis.mapPoints), maxPoints) as GpsCoord[];
  return coords.length >= 12 ? coords : null;
}

export function extractSessionReferencePolylinesForMatching(
  session: MyChronSessionExport,
  maxPoints = 120,
): GpsCoord[][] {
  const racingLaps = racingLapsForAnalysis(session);
  if (racingLaps.length === 0) {
    return [];
  }

  const lapNumbers: number[] = [];
  const seen = new Set<number>();

  function queueLap(lapNumber: number | undefined) {
    if (lapNumber == null || seen.has(lapNumber)) {
      return;
    }

    seen.add(lapNumber);
    lapNumbers.push(lapNumber);
  }

  const bestLap = [...racingLaps].sort((left, right) => left.durationMs - right.durationMs)[0]!;
  queueLap(bestLap.lapNumber);

  const afterOutLap = racingLaps.length > 1 ? racingLaps.slice(1) : racingLaps;
  queueLap(afterOutLap[Math.floor((afterOutLap.length - 1) / 2)]?.lapNumber);
  queueLap(afterOutLap[afterOutLap.length - 1]?.lapNumber);

  for (const lap of afterOutLap) {
    queueLap(lap.lapNumber);
  }

  const polylines: GpsCoord[][] = [];
  const seenShapes = new Set<string>();

  for (const lapNumber of lapNumbers) {
    const polyline = lapPolylineForMatching(session, lapNumber, maxPoints);
    if (!polyline) {
      continue;
    }

    const shapeKey = polyline
      .filter((_, index) => index % 4 === 0)
      .map((point) => `${point.lat.toFixed(5)},${point.lon.toFixed(5)}`)
      .join('|');
    if (seenShapes.has(shapeKey)) {
      continue;
    }

    seenShapes.add(shapeKey);
    polylines.push(polyline);
  }

  return polylines;
}

export function scoreCircuitPolylineAlignment(
  reference: GpsCoord[],
  candidate: GpsCoord[],
  sampleCount = 64,
): { score: number; meanDistanceM: number; reversed: boolean } {
  if (reference.length < 2 || candidate.length < 2) {
    return { score: 0, meanDistanceM: Number.POSITIVE_INFINITY, reversed: false };
  }

  const ref = resamplePolylineByFraction(reference, sampleCount);
  let bestScore = 0;
  let bestMean = Number.POSITIVE_INFINITY;
  let bestReversed = false;

  for (const reversed of [false, true]) {
    const oriented = reversed ? [...candidate].reverse() : candidate;
    const base = resamplePolylineByFraction(oriented, sampleCount);

    for (let shift = 0; shift < sampleCount; shift += 1) {
      const shifted = [...base.slice(shift), ...base.slice(0, shift)];
      const distances = ref.map((point, index) => haversineM(point, shifted[index]!));
      const mean = distances.reduce((sum, value) => sum + value, 0) / distances.length;
      const score = Math.max(0, Math.min(1, 1 - mean / 35));
      if (score > bestScore) {
        bestScore = score;
        bestMean = mean;
        bestReversed = reversed;
      }
    }
  }

  return { score: bestScore, meanDistanceM: bestMean, reversed: bestReversed };
}

type SavedCircuitDraft = Pick<SavedTrackContribution, 'id' | 'trackId' | 'venue' | 'payload' | 'location'>;

function matchPolylineToCircuitDraft(
  sessionPolyline: GpsCoord[],
  session: MyChronSessionExport,
  drafts: SavedCircuitDraft[],
): CircuitDraftMatch | null {
  if (sessionPolyline.length < 12) {
    return null;
  }

  const sessionDirection = detectDirection(sessionPolyline);
  const sessionCenter = sessionGpsCenter(session);
  const sessionLengthM = polylineLengthM(sessionPolyline);

  let best: CircuitDraftMatch | null = null;

  for (const draft of drafts) {
    const draftPolyline = draft.payload.gpsPolyline;
    if (
      draftPolyline.length < CIRCUIT_MATCH_MIN_DRAFT_POINTS
      || !draft.payload.startLineSuggestion?.pointA
      || !draft.payload.startLineSuggestion?.pointB
    ) {
      continue;
    }

    if (sessionCenter) {
      const draftCenter = draft.location
        ? { lat: draft.location.centerLat, lon: draft.location.centerLon }
        : {
          lat: draftPolyline.reduce((sum, point) => sum + point.lat, 0) / draftPolyline.length,
          lon: draftPolyline.reduce((sum, point) => sum + point.lon, 0) / draftPolyline.length,
        };
      const centerDistanceKm = haversineM(sessionCenter, draftCenter) / 1000;
      if (centerDistanceKm > 8) {
        continue;
      }
    }

    const alignment = scoreCircuitPolylineAlignment(draftPolyline, sessionPolyline);
    const lengthRatio =
      sessionLengthM > 0 && draft.payload.lapLengthEstimateM > 0
        ? Math.min(sessionLengthM, draft.payload.lapLengthEstimateM)
          / Math.max(sessionLengthM, draft.payload.lapLengthEstimateM)
        : 0.5;
    if (lengthRatio < 0.72) {
      continue;
    }

    let confidence = alignment.score * (0.82 + lengthRatio * 0.18);
    const directionAligned =
      alignment.reversed
        ? sessionDirection !== draft.payload.direction
        : sessionDirection === draft.payload.direction;

    if (directionAligned) {
      confidence = Math.min(0.99, confidence + (alignment.reversed ? 0.03 : 0.08));
    } else {
      confidence *= 0.45;
    }

    if (alignment.reversed && sessionDirection === draft.payload.direction) {
      confidence *= 0.6;
    }

    if (confidence < CIRCUIT_MATCH_MIN_CONFIDENCE) {
      continue;
    }

    if (!best || confidence > best.confidence) {
      best = {
        draftId: draft.id,
        trackId: draft.trackId,
        circuitName: draft.venue?.trim() || 'Saved circuit',
        direction: draft.payload.direction,
        confidence,
        meanDistanceM: alignment.meanDistanceM,
        directionAligned,
        payload: draft.payload,
      };
    }
  }

  return best;
}

export function matchSessionToCircuitDraft(
  session: MyChronSessionExport,
  drafts: SavedCircuitDraft[],
): CircuitDraftMatch | null {
  const sessionPolylines = extractSessionReferencePolylinesForMatching(session);
  if (sessionPolylines.length === 0) {
    return null;
  }

  let best: CircuitDraftMatch | null = null;
  for (const sessionPolyline of sessionPolylines) {
    const match = matchPolylineToCircuitDraft(sessionPolyline, session, drafts);
    if (match && (!best || match.confidence > best.confidence)) {
      best = match;
    }
  }

  return best;
}

/** Match a raw GPS trace (e.g. GoPro GPMF) to saved circuits without lap boundaries. */
export function matchGpsTraceToCircuitDraft(
  gps: MyChronGpsPoint[],
  drafts: SavedCircuitDraft[],
  maxPoints = 120,
): CircuitDraftMatch | null {
  const coords = downsampleTrackPoints(gpsToCoords(gps), maxPoints) as GpsCoord[];
  if (coords.length < 12) {
    return null;
  }

  const session: MyChronSessionExport = {
    format: 'gopro-gps',
    channels: [],
    laps: [],
    gps,
    series: {},
  };

  return matchPolylineToCircuitDraft(coords, session, drafts);
}

export function geometryFromCircuitDraft(
  payload: SavedTrackContribution['payload'] | TrackContributionPayload,
): RecalibratedTrackGeometry {
  const startFraction =
    payload.startFraction ??
    (payload.gpsPolyline.length >= 2
      ? startFractionForStartLine(payload.gpsPolyline, payload.startLineSuggestion)
      : 0);

  return {
    startFraction,
    polyline: healCircuitPolylineForDisplay(payload.gpsPolyline),
    startLineSuggestion: payload.startLineSuggestion,
    cornerApexSuggestions: payload.cornerApexSuggestions as CornerApexSuggestion[],
    segmentSuggestions: payload.segmentSuggestions,
    direction: payload.direction,
    lapLengthEstimateM: payload.lapLengthEstimateM,
  };
}

export function computeLapQuality(
  session: MyChronSessionExport,
  lapNumber: number,
  referencePolyline: GpsCoord[] | null,
): LapQualityBreakdown {
  const analysis = analyzeLap(session, lapNumber);
  if (!analysis) {
    return {
      lapNumber,
      qualityScore: 0,
      gpsQuality: 0,
      lapCompleteness: 0,
      lineConsistency: 0,
      speedConsistency: 0,
      sampleDensity: 0,
      accepted: false,
      rejectReason: 'Invalid lap',
    };
  }

  const { startMs, endMs } = lapWindow(analysis.lap);
  const gps = gpsPointsInWindow(session.gps, startMs, endMs);
  const coords = gpsToCoords(gps);
  const speeds = gps.map((point) => point.speed * 3.6);

  const gpsQuality = scoreGpsQuality(gps);
  const lapCompleteness = scoreLapCompleteness(gps, analysis.lap.durationMs);
  const sampleDensity = scoreSampleDensity(gps, analysis.lap.durationMs);
  const speedConsistency = scoreSpeedConsistency(speeds);
  const lineConsistency = referencePolyline ? scoreLineConsistency(referencePolyline, coords) : 0.75;

  const qualityScore =
    gpsQuality * 0.35 +
    lapCompleteness * 0.25 +
    lineConsistency * 0.2 +
    speedConsistency * 0.1 +
    sampleDensity * 0.1;

  let rejectReason: string | undefined;
  if (gpsQuality < 0.45) {
    rejectReason = 'Poor GPS';
  } else if (lapCompleteness < 0.5) {
    rejectReason = 'Partial lap';
  } else if (lineConsistency < 0.35 && referencePolyline) {
    rejectReason = 'Line inconsistency (pit/out lap?)';
  } else if (sampleDensity < 0.25) {
    rejectReason = 'Low sample density';
  }

  return {
    lapNumber,
    qualityScore,
    gpsQuality,
    lapCompleteness,
    lineConsistency,
    speedConsistency,
    sampleDensity,
    accepted: qualityScore >= 0.62 && !rejectReason,
    rejectReason,
  };
}

// ── Lap reslicing at start line ──────────────────────────────────────────────

export type ReslicedLapKind = 'flying' | 'out' | 'in' | 'crash';

export type ReslicedLapWindow = ReplayLapWindow & {
  complete: boolean;
  kind: ReslicedLapKind;
};

export type ResliceLapsResult = {
  laps: ReslicedLapWindow[];
  crossingsMs: number[];
};

export function reslicedFlyingLaps(reslice: ResliceLapsResult) {
  return reslice.laps.filter((lap) => lap.kind === 'flying');
}

export function reslicedLapKindCounts(reslice: ResliceLapsResult) {
  const counts = { flying: 0, out: 0, in: 0, crash: 0 };
  for (const lap of reslice.laps) {
    counts[lap.kind] += 1;
  }
  return counts;
}

function gpsCoordFromPoint(point: MyChronGpsPoint): GpsCoord {
  return { lat: point.lat, lon: point.lon };
}

function toLocalMeters(origin: GpsCoord, point: GpsCoord) {
  const latRad = (origin.lat * Math.PI) / 180;
  return {
    x: ((point.lon - origin.lon) * Math.PI) / 180 * Math.cos(latRad) * EARTH_RADIUS_M,
    y: ((point.lat - origin.lat) * Math.PI) / 180 * EARTH_RADIUS_M,
  };
}

export function signedDistanceToStartLineM(
  point: GpsCoord,
  startLine: Pick<StartLineSuggestion, 'pointA' | 'pointB'>,
): number {
  const origin = {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
  const a = toLocalMeters(origin, startLine.pointA);
  const b = toLocalMeters(origin, startLine.pointB);
  const p = toLocalMeters(origin, point);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);

  if (length <= 0) {
    return 0;
  }

  return ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x)) / length;
}

function distanceToStartLineSegmentM(
  point: GpsCoord,
  startLine: Pick<StartLineSuggestion, 'pointA' | 'pointB'>,
): number {
  const origin = {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
  const a = toLocalMeters(origin, startLine.pointA);
  const b = toLocalMeters(origin, startLine.pointB);
  const p = toLocalMeters(origin, point);
  const abX = b.x - a.x;
  const abY = b.y - a.y;
  const lengthSquared = abX * abX + abY * abY;

  if (lengthSquared <= 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  const projection = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * abX + (p.y - a.y) * abY) / lengthSquared),
  );
  const closestX = a.x + abX * projection;
  const closestY = a.y + abY * projection;
  return Math.hypot(p.x - closestX, p.y - closestY);
}

function localMetersToCoord(origin: GpsCoord, point: { x: number; y: number }): GpsCoord {
  const latRad = (origin.lat * Math.PI) / 180;
  return {
    lat: origin.lat + (point.y / EARTH_RADIUS_M) * (180 / Math.PI),
    lon: origin.lon + (point.x / (EARTH_RADIUS_M * Math.cos(latRad))) * (180 / Math.PI),
  };
}

export function projectCoordOntoStartLineSegment(
  coord: GpsCoord,
  startLine: Pick<StartLineSuggestion, 'pointA' | 'pointB'>,
): GpsCoord {
  const origin = {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
  const a = toLocalMeters(origin, startLine.pointA);
  const b = toLocalMeters(origin, startLine.pointB);
  const p = toLocalMeters(origin, coord);
  const abX = b.x - a.x;
  const abY = b.y - a.y;
  const lengthSquared = abX * abX + abY * abY;

  if (lengthSquared <= 0) {
    return coord;
  }

  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abX + (p.y - a.y) * abY) / lengthSquared));
  return localMetersToCoord(origin, { x: a.x + abX * t, y: a.y + abY * t });
}

/** Trim pre-start-line GPS and snap the lap trace origin onto the circuit start/finish line. */
export function prepareCircuitLapMapPoints(
  points: TrackMapPoint[],
  startLine: Pick<StartLineSuggestion, 'pointA' | 'pointB'>,
  startMs: number,
): TrackMapPoint[] {
  if (points.length < 2) {
    return points;
  }

  const searchLimit = Math.min(points.length, 48);
  let startIndex = 0;
  let bestDistance = Math.abs(signedDistanceToStartLineM(points[0]!, startLine));

  for (let index = 1; index < searchLimit; index += 1) {
    const distance = Math.abs(signedDistanceToStartLineM(points[index]!, startLine));
    if (distance < bestDistance) {
      bestDistance = distance;
      startIndex = index;
    }
  }

  const trimmed = points.slice(startIndex);
  if (trimmed.length < 2) {
    return points;
  }

  const snapped = projectCoordOntoStartLineSegment(trimmed[0]!, startLine);
  const first: TrackMapPoint = {
    ...trimmed[0]!,
    lat: snapped.lat,
    lon: snapped.lon,
    timecodeMs: startMs,
  };

  const rest = trimmed.slice(1).filter((point) => point.timecodeMs > startMs);
  return rest.length > 0 ? [first, ...rest] : [first, trimmed.at(-1)!];
}

function crossingTimeOnGpsSegment(
  previous: MyChronGpsPoint,
  current: MyChronGpsPoint,
  startLine: StartLineSuggestion,
): number | null {
  const origin = {
    lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
    lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
  };
  const a = toLocalMeters(origin, startLine.pointA);
  const b = toLocalMeters(origin, startLine.pointB);
  const s0 = toLocalMeters(origin, gpsCoordFromPoint(previous));
  const s1 = toLocalMeters(origin, gpsCoordFromPoint(current));
  const rd = { x: s1.x - s0.x, y: s1.y - s0.y };
  const qd = { x: b.x - a.x, y: b.y - a.y };
  const denom = rd.x * qd.y - rd.y * qd.x;

  if (Math.abs(denom) < 1e-6) {
    return null;
  }

  const t = ((a.x - s0.x) * qd.y - (a.y - s0.y) * qd.x) / denom;
  const u = ((a.x - s0.x) * rd.y - (a.y - s0.y) * rd.x) / denom;
  const segmentMargin = 0.25;

  if (t < 0 || t > 1 || u < -segmentMargin || u > 1 + segmentMargin) {
    return null;
  }

  const d0 = signedDistanceToStartLineM(gpsCoordFromPoint(previous), startLine);
  const d1 = signedDistanceToStartLineM(gpsCoordFromPoint(current), startLine);
  if (d0 * d1 > 0 && Math.min(Math.abs(d0), Math.abs(d1)) > 4) {
    return null;
  }

  return previous.timecodeMs + t * (current.timecodeMs - previous.timecodeMs);
}

function dedupeCrossingTimes(crossingsMs: number[], mergeWithinMs = 1_500): number[] {
  if (crossingsMs.length === 0) {
    return [];
  }

  const sorted = [...crossingsMs].sort((left, right) => left - right);
  const merged = [sorted[0]!];

  for (let index = 1; index < sorted.length; index += 1) {
    const value = sorted[index]!;
    const last = merged.at(-1)!;

    if (value - last <= mergeWithinMs) {
      merged[merged.length - 1] = (last + value) / 2;
      continue;
    }

    merged.push(value);
  }

  return merged;
}

function estimateMinCrossingGapMs(crossingsMs: number[]): number {
  if (crossingsMs.length < 2) {
    return 45_000;
  }

  const gaps: number[] = [];
  for (let index = 1; index < crossingsMs.length; index += 1) {
    gaps.push(crossingsMs[index]! - crossingsMs[index - 1]!);
  }

  gaps.sort((left, right) => left - right);
  const medianGap = gaps[Math.floor(gaps.length / 2)] ?? 60_000;
  return Math.max(15_000, Math.min(60_000, medianGap * 0.42));
}

export function detectStartLineCrossings(
  gps: MyChronGpsPoint[],
  startLine: StartLineSuggestion,
  options?: {
    minCrossingGapMs?: number;
    rearmDistanceM?: number;
  },
): number[] {
  if (gps.length < 2) {
    return [];
  }

  const rearmDistanceM = options?.rearmDistanceM ?? 18;
  const rawCrossings: number[] = [];
  let armed = true;

  for (let index = 1; index < gps.length; index += 1) {
    const previous = gps[index - 1]!;
    const current = gps[index]!;
    // Rearm against the finite start-line segment. Compact circuits can remain
    // close to the line's infinite extension for a whole lap, which otherwise
    // suppresses every crossing after the first one.
    const distanceM = distanceToStartLineSegmentM(gpsCoordFromPoint(current), startLine);

    if (distanceM >= rearmDistanceM) {
      armed = true;
    }

    const crossingMs = crossingTimeOnGpsSegment(previous, current, startLine);
    if (crossingMs === null || !armed) {
      continue;
    }

    rawCrossings.push(crossingMs);
    armed = false;
  }

  const deduped = dedupeCrossingTimes(rawCrossings);
  const minCrossingGapMs = options?.minCrossingGapMs ?? estimateMinCrossingGapMs(deduped);
  const filtered: number[] = [];

  for (const crossingMs of deduped) {
    if (filtered.length === 0 || crossingMs - filtered.at(-1)! >= minCrossingGapMs) {
      filtered.push(crossingMs);
      continue;
    }
  }

  return filtered;
}

function averageSpeedKph(gps: MyChronGpsPoint[]): number {
  if (gps.length === 0) {
    return 0;
  }

  return gps.reduce((sum, point) => sum + point.speed * 3.6, 0) / gps.length;
}

function medianValue(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((left, right) => left - right);
  return sorted[Math.floor(sorted.length / 2)]!;
}

type GpsSegment = {
  startMs: number;
  endMs: number;
  complete: boolean;
  position: 'leading' | 'between' | 'trailing';
};

type SegmentQuality = {
  completeness: number;
  lineConsistency: number | null;
  pathLengthM: number;
  avgSpeedKph: number;
  sampleCount: number;
};

function splitContinuousGpsAtCrossings(
  session: MyChronSessionExport,
  crossingsMs: number[],
): GpsSegment[] {
  if (session.gps.length < 2) {
    return [];
  }

  const sessionStartMs = session.gps[0]!.timecodeMs;
  const sessionEndMs = session.gps.at(-1)!.timecodeMs;

  if (crossingsMs.length === 0) {
    return [
      {
        startMs: sessionStartMs,
        endMs: sessionEndMs,
        complete: false,
        position: 'leading',
      },
    ];
  }

  const segments: GpsSegment[] = [
    {
      startMs: sessionStartMs,
      endMs: crossingsMs[0]!,
      complete: false,
      position: 'leading',
    },
  ];

  for (let index = 0; index < crossingsMs.length - 1; index += 1) {
    segments.push({
      startMs: crossingsMs[index]!,
      endMs: crossingsMs[index + 1]!,
      complete: true,
      position: 'between',
    });
  }

  const lastCrossingMs = crossingsMs.at(-1)!;
  if (sessionEndMs - lastCrossingMs >= 3_000) {
    segments.push({
      startMs: lastCrossingMs,
      endMs: sessionEndMs,
      complete: false,
      position: 'trailing',
    });
  }

  return segments;
}

function scoreSegmentQuality(
  session: MyChronSessionExport,
  segment: GpsSegment,
  referencePolyline?: GpsCoord[],
): SegmentQuality {
  const gps = gpsPointsInWindow(session.gps, segment.startMs, segment.endMs);
  const durationMs = segment.endMs - segment.startMs;
  const coords = gpsToCoords(gps);

  return {
    completeness: scoreLapCompleteness(gps, durationMs),
    lineConsistency:
      referencePolyline && coords.length >= 2
        ? scoreLineConsistency(referencePolyline, coords)
        : null,
    pathLengthM: polylineLengthM(coords),
    avgSpeedKph: averageSpeedKph(gps),
    sampleCount: gps.length,
  };
}

function medianLongerHalf(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const upperHalf = sorted.slice(Math.floor(sorted.length / 2));
  return medianValue(upperHalf.length > 0 ? upperHalf : sorted);
}

function isCrashSegment(
  durationMs: number,
  durationSec: number,
  baselineFlyingDurationSec: number | null,
  quality: SegmentQuality,
  referenceLengthM: number | null,
  minLapDurationMs: number,
  maxLapDurationMs: number,
): boolean {
  if (durationMs < minLapDurationMs || durationMs > maxLapDurationMs) {
    return true;
  }

  if (baselineFlyingDurationSec === null) {
    return false;
  }

  const veryShort = durationSec < Math.min(22, baselineFlyingDurationSec * 0.55);
  const muchShorter = durationSec < baselineFlyingDurationSec * 0.68;
  const halfLap = referenceLengthM !== null && quality.pathLengthM < referenceLengthM * 0.45;
  const stopped = quality.avgSpeedKph > 0 && quality.avgSpeedKph < 8;

  if (veryShort) {
    return true;
  }

  return muchShorter && (halfLap || stopped || quality.sampleCount < 5);
}

function classifyContinuousGpsSegments(
  session: MyChronSessionExport,
  segments: GpsSegment[],
  referencePolyline: GpsCoord[] | undefined,
  minLapDurationMs: number,
  maxLapDurationMs: number,
): ReslicedLapWindow[] {
  if (segments.length === 0) {
    return [];
  }

  const referenceLengthM =
    referencePolyline && referencePolyline.length >= 2 ? polylineLengthM(referencePolyline) : null;

  const segmentMeta = segments.map((segment, index) => {
    const durationMs = segment.endMs - segment.startMs;
    const quality = scoreSegmentQuality(session, segment, referencePolyline);

    return { segment, index, durationMs, quality };
  });

  const betweenDurationSec = segmentMeta
    .filter(
      (entry) =>
        entry.segment.position === 'between' &&
        entry.durationMs >= minLapDurationMs &&
        entry.durationMs <= maxLapDurationMs,
    )
    .map((entry) => entry.durationMs / 1000);
  const baselineFlyingDurationSec = medianLongerHalf(betweenDurationSec);

  return segmentMeta.map(({ segment, durationMs, quality }, index) => {
    const durationSec = durationMs / 1000;
    let kind: ReslicedLapKind = 'flying';

    if (segment.position === 'trailing') {
      kind = 'in';
    } else if (segment.position === 'leading') {
      kind = 'out';
    } else if (
      isCrashSegment(
        durationMs,
        durationSec,
        baselineFlyingDurationSec,
        quality,
        referenceLengthM,
        minLapDurationMs,
        maxLapDurationMs,
      )
    ) {
      kind = 'crash';
    }

    return {
      lapNumber: index + 1,
      startMs: segment.startMs,
      endMs: segment.endMs,
      durationSec: durationMs / 1000,
      complete: segment.complete,
      kind,
    };
  });
}

export function resliceLapsAtStartLine(
  session: MyChronSessionExport,
  startLine: StartLineSuggestion,
  options?: {
    minLapDurationMs?: number;
    maxLapDurationMs?: number;
    minCrossingGapMs?: number;
    referencePolyline?: GpsCoord[];
  },
): ResliceLapsResult {
  const minLapDurationMs = options?.minLapDurationMs ?? 15_000;
  const maxLapDurationMs = options?.maxLapDurationMs ?? 120_000;
  const crossingsMs = detectStartLineCrossings(session.gps, startLine, {
    minCrossingGapMs: options?.minCrossingGapMs,
  });

  const segments = splitContinuousGpsAtCrossings(session, crossingsMs);
  const laps = classifyContinuousGpsSegments(
    session,
    segments,
    options?.referencePolyline,
    minLapDurationMs,
    maxLapDurationMs,
  );

  return { laps, crossingsMs };
}

export function buildReslicedLapReplayTimeline(
  reslice: ResliceLapsResult,
  lapNumber: number,
): ReplayTimeline | null {
  const lap = reslice.laps.find((entry) => entry.lapNumber === lapNumber);
  if (!lap) {
    return null;
  }

  return {
    scope: 'lap',
    durationSec: lap.durationSec,
    startTimecodeMs: lap.startMs,
    endTimecodeMs: lap.endMs,
    laps: [lap],
  };
}

export function buildReslicedSessionReplayTimeline(reslice: ResliceLapsResult): ReplayTimeline | null {
  const laps = reslice.laps;
  if (laps.length === 0) {
    return null;
  }

  const startTimecodeMs = laps[0]!.startMs;
  const endTimecodeMs = laps.at(-1)!.endMs;

  return {
    scope: 'session',
    durationSec: (endTimecodeMs - startTimecodeMs) / 1000,
    startTimecodeMs,
    endTimecodeMs,
    laps,
  };
}

export function buildMapPointGroupsForReslicedLaps(
  session: MyChronSessionExport,
  laps: ReplayLapWindow[],
  maxPointsPerLap = 800,
): TrackMapPoint[][] {
  return laps
    .map((lap) => buildMapPointsForGpsWindow(session, lap.startMs, lap.endMs, maxPointsPerLap))
    .filter((group) => group.length >= 2);
}

// ── Feature extraction ───────────────────────────────────────────────────────

function detectStartLineFromPolyline(polyline: GpsCoord[]): StartLineSuggestion {
  return buildStartLineAtFraction(polyline, 0);
}

function extractContributionFromSession(
  session: MyChronSessionExport,
  trackId: string,
  acceptedLapNumbers: number[],
): TrackContributionPayload | null {
  if (acceptedLapNumbers.length === 0) {
    return null;
  }

  const sessionAnalysis = analyzeSession(session);
  const bestLapNumber = sessionAnalysis?.bestLapNumber ?? acceptedLapNumbers[0]!;
  const bestAnalysis = analyzeLap(session, bestLapNumber);
  if (!bestAnalysis) {
    return null;
  }

  const polylines = acceptedLapNumbers
    .map((lapNumber) => {
      const lapAnalysis = analyzeLap(session, lapNumber);
      return lapAnalysis ? mapPointsToCoords(lapAnalysis.mapPoints) : [];
    })
    .filter((polyline) => polyline.length >= 2);

  const merged = polylines.reduce<GpsCoord[]>((accumulator, polyline) => {
    return accumulator.length >= polyline.length ? accumulator : polyline;
  }, polylines[0] ?? []);

  const polyline = healCircuitPolylineForDisplay(merged, 3, 600);
  const startLine = detectStartLineFromPolyline(polyline);
  const corners = detectCornersFromPolyline(polyline);
  const segments = detectSegmentsFromPolyline(polyline);
  const direction = detectDirection(polyline);
  const lapLengthEstimateM = polylineLengthM(polyline);

  const avgQuality =
    acceptedLapNumbers.length > 0
      ? acceptedLapNumbers.reduce((sum, lapNumber) => sum + computeLapQuality(session, lapNumber, polyline).qualityScore, 0) /
        acceptedLapNumbers.length
      : 0.5;

  return {
    trackId,
    gpsPolyline: polyline,
    startLineSuggestion: startLine,
    segmentSuggestions: segments,
    cornerApexSuggestions: corners,
    direction,
    lapLengthEstimateM,
    confidenceScore: Math.min(0.99, avgQuality * 0.92 + 0.06),
  };
}

// ── Track matching & comparison ─────────────────────────────────────────────

export function resolveCatalogTrackId(session: MyChronSessionExport): string | null {
  const match = matchCatalogTrack(session);
  return match.id === 'unknown' ? null : match.id;
}

function matchCatalogTrack(session: MyChronSessionExport): ResolvedTrackMatch {
  const center = sessionGpsCenter(session);

  for (const track of TRACK_CATALOG) {
    if (center) {
      const distKm = haversineM(center, track.center) / 1000;
      if (distKm < 3) {
        return {
          id: track.id,
          name: track.name,
          confidence: Math.max(0.72, 0.96 - distKm * 0.08),
          method: 'catalog',
        };
      }
    }
  }

  return {
    id: 'unknown',
    name: 'Unknown Track',
    confidence: 0.35,
    method: 'unknown',
  };
}

function resolveFreshTrackMatch(
  session: MyChronSessionExport,
  savedTracks: SavedTrack[],
): ResolvedTrackMatch {
  const center = sessionGpsCenter(session);

  if (center && savedTracks.length > 0) {
    const nearest = findNearestTrack(savedTracks, center.lat, center.lon, 5);
    if (nearest) {
      const distanceKm = trackDistanceKm(nearest, center.lat, center.lon);
      return {
        id: nearest.id,
        name: nearest.name,
        confidence: Math.min(0.9, Math.max(0.68, 0.94 - distanceKm * 0.05)),
        method: 'gps',
      };
    }
  }

  return matchCatalogTrack(session);
}

function isSessionContextPlausible(session: MyChronSessionExport, track: SavedTrack) {
  const center = sessionGpsCenter(session);
  const distanceKm = center ? trackDistanceKm(track, center.lat, center.lon) : null;
  return distanceKm !== null && distanceKm <= 5;
}

function freshMatchOverridesContext(
  _session: MyChronSessionExport,
  boundTrack: SavedTrack,
  fresh: ResolvedTrackMatch,
) {
  if (fresh.method === 'unknown' || fresh.id === boundTrack.id) {
    return false;
  }

  if (fresh.method === 'gps' && fresh.confidence >= 0.8) {
    return true;
  }

  return !isSessionContextPlausible(_session, boundTrack) && fresh.confidence >= 0.72;
}

function resolveManualTrackMatch(
  trackId: string,
  savedTracks: SavedTrack[],
): ResolvedTrackMatch | null {
  const saved = savedTracks.find((track) => track.id === trackId);
  if (saved) {
    return {
      id: saved.id,
      name: saved.name,
      confidence: 1,
      method: 'manual',
    };
  }

  const catalog = TRACK_CATALOG.find((entry) => entry.id === trackId);
  if (catalog) {
    return {
      id: catalog.id,
      name: catalog.name,
      confidence: 1,
      method: 'manual',
    };
  }

  return null;
}

export function resolveTrackForSession(
  session: MyChronSessionExport,
  options?: TrackMatchOptions,
): ResolvedTrackMatch {
  const savedTracks = options?.savedTracks ?? [];
  const fresh = resolveFreshTrackMatch(session, savedTracks);

  if (options?.manualTrackId) {
    const manual = resolveManualTrackMatch(options.manualTrackId, savedTracks);
    if (manual) {
      return manual;
    }
  }

  if (options?.circuitDraftMatch) {
    const match = options.circuitDraftMatch;
    return {
      id: match.trackId,
      name: match.circuitName,
      confidence: match.confidence,
      method: 'circuit-draft',
    };
  }

  if (options?.preferredTrackId) {
    const bound = savedTracks.find((track) => track.id === options.preferredTrackId);
    if (bound) {
      if (freshMatchOverridesContext(session, bound, fresh)) {
        return fresh;
      }

      if (isSessionContextPlausible(session, bound)) {
        const center = sessionGpsCenter(session);
        const distanceKm = center ? trackDistanceKm(bound, center.lat, center.lon) : 0;
        return {
          id: bound.id,
          name: bound.name,
          confidence: distanceKm <= 3 ? 0.98 : distanceKm <= 8 ? 0.92 : 0.86,
          method: 'session-context',
        };
      }
    }
  }

  return fresh;
}

function degradeForMockGlobal(payload: TrackContributionPayload): TrackContributionPayload {
  const sparsePolyline = downsampleTrackPoints(payload.gpsPolyline, 48) as GpsCoord[];
  const shiftedStart = perpOffsetStartLine(payload.startLineSuggestion, 18);
  const shiftedSegments = payload.segmentSuggestions.map((segment, index) => ({
    ...segment,
    startFraction: Math.max(0, segment.startFraction + (index === 0 ? 0.04 : 0)),
    endFraction: Math.min(1, segment.endFraction + (index === 2 ? -0.03 : 0)),
    confidence: segment.confidence - 0.12,
  }));

  return {
    ...payload,
    gpsPolyline: sparsePolyline,
    startLineSuggestion: shiftedStart,
    segmentSuggestions: shiftedSegments,
    cornerApexSuggestions: payload.cornerApexSuggestions.map((corner, index) => {
      const shifted = offsetCoord(corner, index % 2 === 0 ? -6 : 6, index % 2 === 0 ? 4 : -4);
      return {
        ...corner,
        lat: shifted.lat,
        lon: shifted.lon,
        confidence: corner.confidence - 0.18,
      };
    }),
    confidenceScore: payload.confidenceScore - 0.14,
  };
}

function compareProfiles(
  incoming: TrackContributionPayload,
  baseline: TrackContributionPayload,
): EnrichmentFinding[] {
  const findings: EnrichmentFinding[] = [];

  const startShift = haversineM(
    {
      lat: (incoming.startLineSuggestion.pointA.lat + incoming.startLineSuggestion.pointB.lat) / 2,
      lon: (incoming.startLineSuggestion.pointA.lon + incoming.startLineSuggestion.pointB.lon) / 2,
    },
    {
      lat: (baseline.startLineSuggestion.pointA.lat + baseline.startLineSuggestion.pointB.lat) / 2,
      lon: (baseline.startLineSuggestion.pointA.lon + baseline.startLineSuggestion.pointB.lon) / 2,
    },
  );

  if (startShift > 8) {
    findings.push({
      id: 'start-line',
      kind: 'start_line',
      title: 'Better start/finish crossing',
      detail: `Suggested line is ${startShift.toFixed(0)} m from community baseline with higher crossing confidence.`,
      confidence: incoming.startLineSuggestion.confidence,
      improvementScore: Math.min(1, startShift / 25),
    });
  }

  const incomingLen = polylineLengthM(incoming.gpsPolyline);
  const baselineLen = polylineLengthM(baseline.gpsPolyline);
  const pointDelta =
    scoreLineConsistency(incoming.gpsPolyline, baseline.gpsPolyline) < 0.92
      ? 1 - scoreLineConsistency(incoming.gpsPolyline, baseline.gpsPolyline)
      : 0;

  if (incoming.gpsPolyline.length > baseline.gpsPolyline.length * 1.4 || pointDelta > 0.08) {
    findings.push({
      id: 'outline',
      kind: 'outline',
      title: 'Cleaner track outline',
      detail: `${incoming.gpsPolyline.length} vs ${baseline.gpsPolyline.length} polyline points · ${Math.abs(incomingLen - baselineLen).toFixed(0)} m length delta.`,
      confidence: incoming.confidenceScore,
      improvementScore: Math.min(1, pointDelta + 0.25),
    });
  }

  const newCorners = incoming.cornerApexSuggestions.filter((corner) => {
    return !baseline.cornerApexSuggestions.some(
      (existing) => haversineM(existing, corner) < 12,
    );
  });

  if (newCorners.length > 0) {
    findings.push({
      id: 'corners',
      kind: 'corner',
      title: 'New corner apex estimate',
      detail: `${newCorners.length} apex point(s) refine community map geometry.`,
      confidence: Math.max(...newCorners.map((corner) => corner.confidence)),
      improvementScore: Math.min(1, newCorners.length * 0.2),
    });
  }

  const segmentShift = incoming.segmentSuggestions.reduce((max, segment, index) => {
    const base = baseline.segmentSuggestions[index];
    if (!base) {
      return max;
    }
    return Math.max(
      max,
      Math.abs(segment.startFraction - base.startFraction) + Math.abs(segment.endFraction - base.endFraction),
    );
  }, 0);

  if (segmentShift > 0.04) {
    findings.push({
      id: 'segments',
      kind: 'segment',
      title: 'Updated segment timing zones',
      detail: 'Sector boundaries align better with distance-based splits from clean laps.',
      confidence: 0.84,
      improvementScore: Math.min(1, segmentShift * 4),
    });
  }

  return findings.sort((left, right) => right.improvementScore - left.improvementScore);
}

function buildGlobalProfile(trackId: string, trackName: string, official: TrackContributionPayload): TrackProfile {
  const catalog = TRACK_CATALOG.find((entry) => entry.id === trackId);
  return {
    id: trackId,
    name: trackName,
    aliases: catalog?.aliases ?? [],
    center: catalog?.center ?? official.gpsPolyline[0] ?? { lat: 0, lon: 0 },
    officialRevision: official,
    communitySuggestions: [],
    teamOverrides: [],
  };
}

// ── Pipeline ─────────────────────────────────────────────────────────────────

const PIPELINE_STEP_DEFS = [
  { id: 'import', label: 'Import session' },
  { id: 'location', label: 'Detect location' },
  { id: 'match', label: 'Match or create track candidate' },
  { id: 'normalize', label: 'Normalize clean flying laps' },
  { id: 'shape', label: 'Extract track shape' },
  { id: 'start', label: 'Detect start line' },
  { id: 'corners', label: 'Detect corners' },
  { id: 'compare', label: 'Compare against global profile' },
  { id: 'suggest', label: 'Suggest improvements' },
  { id: 'approve', label: 'Require approval before merge' },
] as const;

export function runEnrichmentPipeline(
  session: MyChronSessionExport,
  sessionKey: string,
  options?: TrackMatchOptions,
): EnrichmentPipelineResult | null {
  if (!isMyChronSessionExport(session)) {
    return null;
  }

  const steps: EnrichmentPipelineStep[] = PIPELINE_STEP_DEFS.map((step) => ({
    ...step,
    status: 'pending' as const,
  }));

  const setStep = (id: string, status: EnrichmentPipelineStep['status'], detail?: string) => {
    const step = steps.find((entry) => entry.id === id);
    if (step) {
      step.status = status;
      step.detail = detail;
    }
  };

  setStep('import', 'done', `${session.gps.length.toLocaleString()} GPS samples · ${session.laps.length} laps`);

  const location = sessionGpsCenter(session);
  setStep(
    'location',
    location ? 'done' : 'failed',
    location ? `${location.lat.toFixed(5)}, ${location.lon.toFixed(5)}` : 'No GPS center',
  );

  const matchedTrack = resolveTrackForSession(session, options);
  setStep(
    'match',
    matchedTrack.method === 'unknown' ? 'failed' : 'done',
    `${matchedTrack.name} · ${matchedTrack.method} · ${Math.round(matchedTrack.confidence * 100)}%`,
  );

  const racingLaps = racingLapsForAnalysis(session);
  if (racingLaps.length === 0) {
    setStep('normalize', 'failed', 'No racing laps found');
    return null;
  }

  const bestLap = [...racingLaps].sort((left, right) => left.durationMs - right.durationMs)[0]!;
  const bestAnalysis = analyzeLap(session, bestLap.lapNumber);
  const referencePolyline = bestAnalysis ? mapPointsToCoords(bestAnalysis.mapPoints) : null;

  const lapQualities = racingLaps.map((lap) =>
    computeLapQuality(session, lap.lapNumber, referencePolyline),
  );
  const acceptedLapNumbers = lapQualities.filter((entry) => entry.accepted).map((entry) => entry.lapNumber);

  setStep(
    'normalize',
    acceptedLapNumbers.length > 0 ? 'done' : 'failed',
    `${acceptedLapNumbers.length}/${racingLaps.length} laps passed quality gate`,
  );

  const contributionPayload = extractContributionFromSession(session, matchedTrack.id, acceptedLapNumbers);
  if (!contributionPayload) {
    setStep('shape', 'failed', 'Could not extract track shape');
    return null;
  }

  setStep('shape', 'done', `${contributionPayload.gpsPolyline.length} polyline points`);
  setStep('start', 'done', `${Math.round(contributionPayload.startLineSuggestion.confidence * 100)}% start line confidence`);
  setStep('corners', 'done', `${contributionPayload.cornerApexSuggestions.length} apex points`);

  const mockGlobalOfficial = degradeForMockGlobal(contributionPayload);
  const globalProfile = buildGlobalProfile(matchedTrack.id, matchedTrack.name, mockGlobalOfficial);

  setStep('compare', 'done', `Compared to ${globalProfile.name} community baseline`);

  const findings = compareProfiles(contributionPayload, mockGlobalOfficial);
  setStep(
    'suggest',
    findings.length > 0 ? 'done' : 'skipped',
    findings.length > 0 ? `${findings.length} improvement(s) detected` : 'No improvements over baseline',
  );
  setStep('approve', 'pending', 'Awaiting user or admin approval');

  const contribution: TrackContribution = {
    id: `contrib-${sessionKey}`,
    sessionKey,
    venue: session.metadata?.venue ?? 'Unknown',
    status: 'pending',
    payload: contributionPayload,
    qualityScore: contributionPayload.confidenceScore,
    matchedTrackName: matchedTrack.name,
    matchConfidence: matchedTrack.confidence,
    findings,
    createdAt: new Date().toISOString(),
  };

  return {
    sessionKey,
    venue: session.metadata?.venue ?? 'Unknown',
    location,
    matchedTrack,
    lapQualities,
    acceptedLapNumbers,
    contribution,
    globalProfile,
    steps,
  };
}

export function runGoProEnrichmentPipeline(
  session: MyChronSessionExport,
  sessionKey: string,
  options?: TrackMatchOptions,
): EnrichmentPipelineResult | null {
  if (session.format !== 'gopro-gps' || session.gps.length < 12) {
    return null;
  }

  const steps: EnrichmentPipelineStep[] = PIPELINE_STEP_DEFS.map((step) => ({
    ...step,
    status: 'pending' as const,
  }));

  const setStep = (id: string, status: EnrichmentPipelineStep['status'], detail?: string) => {
    const step = steps.find((entry) => entry.id === id);
    if (step) {
      step.status = status;
      step.detail = detail;
    }
  };

  setStep('import', 'done', `${session.gps.length.toLocaleString()} GoPro GPS samples`);

  const location = sessionGpsCenter(session);
  setStep(
    'location',
    location ? 'done' : 'failed',
    location ? `${location.lat.toFixed(5)}, ${location.lon.toFixed(5)}` : 'No GPS center',
  );

  const matchedTrack = resolveTrackForSession(session, options);
  setStep(
    'match',
    matchedTrack.method === 'unknown' ? 'failed' : 'done',
    `${matchedTrack.name} · ${matchedTrack.method} · ${Math.round(matchedTrack.confidence * 100)}%`,
  );

  const primaryGps = selectPrimaryGoProGpsSegment(session.gps);
  const rawPolyline = downsampleTrackPoints(gpsToCoords(primaryGps), 600) as GpsCoord[];
  if (rawPolyline.length < 12) {
    setStep('shape', 'failed', 'Not enough GPS points for a circuit outline');
    return null;
  }

  const polyline = healCircuitPolylineForDisplay(rawPolyline, 3, 600);
  const geometry = recalibrateTrackGeometry(polyline, 0);
  const contributionPayload = buildContributionDraftPayload(
    matchedTrack.id,
    geometry,
    geometry.cornerApexSuggestions,
    Math.min(0.85, matchedTrack.confidence * 0.9 + 0.1),
  );

  setStep('normalize', 'skipped', 'GoPro trace — lap slicing happens in the editor');
  setStep('shape', 'done', `${contributionPayload.gpsPolyline.length} polyline points`);
  setStep(
    'start',
    'done',
    `${Math.round(contributionPayload.startLineSuggestion.confidence * 100)}% start line confidence`,
  );
  setStep('corners', 'done', `${contributionPayload.cornerApexSuggestions.length} apex points`);

  const mockGlobalOfficial = degradeForMockGlobal(contributionPayload);
  const globalProfile = buildGlobalProfile(matchedTrack.id, matchedTrack.name, mockGlobalOfficial);
  const findings = compareProfiles(contributionPayload, mockGlobalOfficial);

  setStep('compare', 'done', `Compared to ${globalProfile.name} community baseline`);
  setStep(
    'suggest',
    findings.length > 0 ? 'done' : 'skipped',
    findings.length > 0 ? `${findings.length} improvement(s) detected` : 'No improvements over baseline',
  );
  setStep('approve', 'pending', 'Awaiting user or admin approval');

  const contribution: TrackContribution = {
    id: `contrib-${sessionKey}`,
    sessionKey,
    venue: session.metadata?.venue ?? 'GoPro footage',
    status: 'draft',
    payload: contributionPayload,
    qualityScore: contributionPayload.confidenceScore,
    matchedTrackName: matchedTrack.name,
    matchConfidence: matchedTrack.confidence,
    findings,
    createdAt: new Date().toISOString(),
  };

  return {
    sessionKey,
    venue: session.metadata?.venue ?? 'GoPro footage',
    location,
    matchedTrack,
    lapQualities: [],
    acceptedLapNumbers: [],
    contribution,
    globalProfile,
    steps,
  };
}

export function runManualTrackEnrichmentPipeline(options: {
  trackId: string;
  trackName: string;
  center: GpsCoord;
  venue?: string;
  sessionKey?: string;
}): EnrichmentPipelineResult {
  const sessionKey =
    options.sessionKey ?? `${MANUAL_MAP_SESSION_KEY_PREFIX}${options.trackId}-${Date.now()}`;
  const geometry = buildManualTrackGeometry({
    mapCenter: options.center,
    startLine: null,
    corners: [],
  });
  const contributionPayload = buildContributionDraftPayload(options.trackId, geometry, [], 0.55);
  const matchedTrack: ResolvedTrackMatch = {
    id: options.trackId,
    name: options.trackName,
    confidence: 1,
    method: 'manual',
  };
  const mockGlobalOfficial = degradeForMockGlobal(contributionPayload);
  const globalProfile = buildGlobalProfile(options.trackId, options.trackName, mockGlobalOfficial);
  const venue = options.venue?.trim() || options.trackName;

  const steps: EnrichmentPipelineStep[] = PIPELINE_STEP_DEFS.map((step) => {
    switch (step.id) {
      case 'import':
        return { ...step, status: 'skipped', detail: 'Map layout (no telemetry session)' };
      case 'location':
        return {
          ...step,
          status: 'done',
          detail: `${options.center.lat.toFixed(5)}, ${options.center.lon.toFixed(5)}`,
        };
      case 'match':
        return { ...step, status: 'done', detail: `${options.trackName} · manual · 100%` };
      case 'normalize':
        return { ...step, status: 'skipped', detail: 'No laps — trace the circuit on the map' };
      case 'shape':
        return { ...step, status: 'pending', detail: 'Click the map to trace the circuit outline' };
      case 'start':
        return { ...step, status: 'pending', detail: 'Close the lap, then place start/finish' };
      case 'corners':
        return { ...step, status: 'pending', detail: 'Close the lap, then add turn markers' };
      case 'compare':
        return { ...step, status: 'skipped', detail: 'No session baseline to compare' };
      case 'suggest':
        return { ...step, status: 'skipped' };
      case 'approve':
        return { ...step, status: 'pending', detail: 'Save draft when layout is ready' };
    }
  });

  const contribution: TrackContribution = {
    id: `contrib-${sessionKey}`,
    sessionKey,
    venue,
    status: 'pending',
    payload: contributionPayload,
    qualityScore: contributionPayload.confidenceScore,
    matchedTrackName: options.trackName,
    matchConfidence: 1,
    findings: [],
    createdAt: new Date().toISOString(),
  };

  return {
    sessionKey,
    venue,
    location: options.center,
    matchedTrack,
    lapQualities: [],
    acceptedLapNumbers: [],
    contribution,
    globalProfile,
    steps,
  };
}

export async function loadSeedSession(url: string): Promise<MyChronSessionExport> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load session (${response.status})`);
  }

  const payload = await response.json();
  if (!isMyChronSessionExport(payload)) {
    throw new Error('Invalid MyChron session export');
  }

  return payload;
}

export function fractionToPolylinePoint(polyline: GpsCoord[], fraction: number): GpsCoord | null {
  return resamplePolylineByFraction(polyline, 200)[Math.min(199, Math.max(0, Math.round(fraction * 199)))] ?? null;
}
